import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  assessSkillQualityGateDefinitions,
  isBlockingScanWarning,
  DEFAULT_RESULT_EXPORT_MAX_RECORDS,
  MAX_RESULT_EXPORT_RECORDS,
  MAX_ORG_SCAN_OBJECTS,
  SYSTEM_BASELINE_SCENARIO_KEY
} from "@ai-sales/contracts";
import type {
  AuditEvent,
  AssistantProfile,
  AssistantPolicy,
  BusinessScenario,
  BusinessScenarioCapability,
  CompanyBusinessProfile,
  KnowledgeCapabilitySnapshot,
  ModelCatalog,
  ModelProfile,
  ModelProvider,
  OrganizationConnection,
  OrganizationDiff,
  OrganizationSnapshot,
  OrgAnalysisRun,
  ObjectCapabilitySummary,
  ObjectDirectoryEntry,
  OrgScanScope,
  PlatformAccountType,
  PlatformRuntimeOverview,
  PlatformSystemStatus,
  PlatformUser,
  SalesforceOAuthGrant,
  SalesforceModelCredentialMigrationStatus,
  SchemaFingerprint,
  SkillCandidate,
  SkillManifest,
  SkillQualityGateRequirement,
  SkillTestCaseV2,
  TestDataset,
  TestRun
} from "@ai-sales/contracts";
import { api } from "./api.js";
import { AssistantProfileSettings } from "./AssistantProfileSettings.js";
import { CapabilityStatusPanel } from "./CapabilityStatusPanel.js";
import { CompanyProfileForm } from "./CompanyProfileForm.js";
import { ScenarioStudioWizard } from "./ScenarioStudioWizard.js";
import { OrgObjectSelector } from "./OrgObjectSelector.js";
import { BusinessScenarioLifecycle } from "./BusinessScenarioLifecycle.js";
import { ReleaseEvidenceSummary } from "./ReleaseEvidenceSummary.js";
import { assessReleaseEvidence } from "./release-evidence.js";
import { useSalesforceAdminBridge } from "./salesforce-admin-bridge.js";
import { CheckIcon, HistoryIcon, LayersIcon, SearchIcon, SettingsIcon, SparklesIcon } from "./icons.js";
import { microphoneStartErrorMessage } from "./media-permissions.js";

type AdminSection = "overview" | "capabilities" | "access" | "audit" | "settings";

interface AdminData {
  connection?: OrganizationConnection;
  companyProfile: CompanyBusinessProfile | null;
  knowledgeCapabilities: KnowledgeCapabilitySnapshot[];
  analysisRuns: OrgAnalysisRun[];
  schemaFingerprints: SchemaFingerprint[];
  users: PlatformUser[];
  scopes: OrgScanScope[];
  snapshots: OrganizationSnapshot[];
  diffs: OrganizationDiff[];
  candidates: SkillCandidate[];
  objectCapabilities: ObjectCapabilitySummary[];
  objectDirectory: ObjectDirectoryEntry[];
  businessScenarios: BusinessScenario[];
  skills: SkillManifest[];
  testDatasets: TestDataset[];
  testRuns: TestRun[];
  providers: ModelProvider[];
  profiles: ModelProfile[];
  modelCredentialMigration: SalesforceModelCredentialMigrationStatus;
  auditEvents: AuditEvent[];
  grants: SalesforceOAuthGrant[];
  systemStatus?: PlatformSystemStatus;
  runtimeOverview?: PlatformRuntimeOverview;
  assistantProfile?: AssistantProfile;
  assistantPolicy?: Pick<AssistantPolicy, "attachmentsEnabled" | "attachmentBatchWritesEnabled" | "resultExport">;
}

interface ActionFeedback {
  name: string;
  status: "running" | "success" | "error";
  message: string;
}

const emptyData: AdminData = {
  companyProfile: null, knowledgeCapabilities: [], analysisRuns: [], schemaFingerprints: [],
  users: [], scopes: [], snapshots: [], diffs: [], candidates: [], objectCapabilities: [], objectDirectory: [], businessScenarios: [], skills: [], testDatasets: [], testRuns: [], providers: [], profiles: [],
  modelCredentialMigration: { state: "not_required", legacyProviderCount: 0, legacyRoles: [], activeLegacyRoles: [], missingRuntimeRoles: [], unmappedLegacyProviderCount: 0, authorizationReady: false, credentialConfigured: null },
  auditEvents: [], grants: []
};
const isLocalDevelopment = ["localhost", "127.0.0.1"].includes(window.location.hostname);

function expectedTestOutcomeLabel(testCase: SkillTestCaseV2): string {
  const outcome = testCase.expectedOutcome ?? (testCase.expectedActionSequence.length ? "action_sequence" : "no_action");
  if (outcome === "no_action") return "不生成 Action";
  if (outcome === "planning_error") return "规划必须失败";
  if (outcome === "planning_timeout") return "规划必须超时关闭";
  return testCase.expectedActionSequence.map((action) => `${action.operation}:${action.objectApiName}`).join(" → ");
}

const qualityGateLabels: Partial<Record<NonNullable<SkillTestCaseV2["qualityGate"]>, string>> = {
  query: "查询", aggregate: "统计", write_preview: "写入预览", cross_object_sequence: "跨对象序列",
  permission_denied: "权限拒绝", unopened_object: "未开放对象", ambiguity: "歧义澄清",
  required_fields: "必填字段", validation_rule: "验证规则", cancellation: "取消",
  duplicate: "重复处理", planning_timeout: "规划超时", invalid_model_output: "非法模型输出"
};

function qualityGateLabel(testCase: SkillTestCaseV2): string {
  return testCase.qualityGate ? qualityGateLabels[testCase.qualityGate] ?? testCase.qualityGate : "自定义";
}

function qualityRequirementLabel(requirement: SkillQualityGateRequirement): string {
  const gate = qualityGateLabels[requirement.gate] ?? requirement.gate;
  if (!requirement.objectApiName) return gate;
  return `${gate}（${requirement.objectApiName}${requirement.operation ? `/${requirement.operation}` : ""}）`;
}

function testResultDescription(result: TestRun["results"][number]): string {
  if (result.status === "failed") return result.error ?? "实际结果与预期不一致";
  if (result.expectedOutcome === "no_action") return "已正确阻止生成 Action";
  if (result.expectedOutcome === "planning_error") return "非法规划已被结构化边界拒绝";
  if (result.expectedOutcome === "planning_timeout") return "模型超时已安全关闭，未生成 Action";
  return "动作序列和对象符合预期";
}

function testResultExpectation(result: TestRun["results"][number]): string {
  if (result.expectedOutcome === "no_action") return "预期：不生成 Action";
  if (result.expectedOutcome === "planning_error") return "预期：规划错误";
  if (result.expectedOutcome === "planning_timeout") return "预期：超时关闭";
  return `预期：${result.expectedOperation ?? "动作"}:${result.expectedObject ?? "对象"}`;
}

const sectionLabels: Record<AdminSection, string> = {
  overview: "总览",
  capabilities: "能力中心",
  access: "用户与权限",
  audit: "运行与审计",
  settings: "系统设置"
};

const accountTypeLabels: Record<PlatformAccountType, string> = {
  admin: "管理员",
  user: "普通用户"
};

type ModelCapabilityName = keyof NonNullable<ModelProvider["capabilityProbe"]>["checks"];
type ModelCapabilityStatus = NonNullable<ModelProvider["capabilityProbe"]>["checks"][ModelCapabilityName];

const modelCapabilityLabels: Record<ModelCapabilityName, string> = {
  text: "文本",
  chinese: "中文",
  structuredPlanning: "结构化规划",
  jsonOutput: "JSON 输出",
  jsonSchema: "JSON Schema",
  toolCalling: "Tool Calling",
  vision: "图片理解",
  documentUnderstanding: "视觉文档理解",
  ocr: "OCR",
  speechToText: "语音转文字"
};

function modelCapabilityNames(provider: ModelProvider, status: ModelCapabilityStatus): string[] {
  const checks = provider.capabilityProbe?.checks;
  if (!checks) return [];
  return (Object.entries(checks) as Array<[ModelCapabilityName, ModelCapabilityStatus]>)
    .filter(([, capabilityStatus]) => capabilityStatus === status)
    .map(([name]) => modelCapabilityLabels[name]);
}

const accountTypeOf = (user: PlatformUser): PlatformAccountType =>
  user.accountType ?? (user.roles.some((role) => role === "platform_admin" || role === "skill_admin")
    ? "admin"
    : "user");

function effectiveSelectionDraft(data: Pick<AdminData, "scopes" | "objectDirectory">): Record<string, OrgScanScope["operations"]> {
  const activeScopes = data.scopes.filter((scope) => scope.enabled && scope.maintenanceState === "active");
  return Object.fromEntries(activeScopes.map((scope) => [scope.objectApiName, scope.operations]));
}

function selectionSignature(selections: Record<string, OrgScanScope["operations"]>): string {
  const normalized: Array<[string, string[]]> = Object.entries(selections)
    .filter(([, operations]) => operations.length)
    .map(([objectApiName, operations]) => [objectApiName, [...operations].sort()] as [string, string[]])
    .sort(([left], [right]) => left.localeCompare(right));
  return JSON.stringify(normalized);
}

export function AdminApp() {
  const salesforceBridge = useSalesforceAdminBridge();
  const [section, setSection] = useState<AdminSection>("overview");
  const [identity, setIdentity] = useState<{ user: PlatformUser; organizationId: string }>();
  const [data, setData] = useState<AdminData>(emptyData);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [actionFeedback, setActionFeedback] = useState<ActionFeedback>();

  const loadData = useCallback(async (_user: PlatformUser) => {
    const [connection, companyProfile, knowledgeCapabilities, analysisRuns, schemaFingerprints, scopes, snapshots, diffs, candidates, objectCapabilities, objectDirectory, businessScenarios, skills, testDatasets, testRuns, users, providers, profiles, modelCredentialMigration, auditEvents, grants, systemStatus, runtimeOverview, assistantProfile, assistantPolicy] = await Promise.all([
      api.getOrgConnection(),
      api.getCompanyProfile(),
      api.listKnowledgeCapabilities(),
      api.listOrgAnalysisRuns(),
      api.listSchemaFingerprints(),
      api.listOrgScanScope(),
      api.listOrgSnapshots(),
      api.listOrgDiffs(),
      api.listSkillCandidates(),
      api.listObjectCapabilities(),
      api.listObjectDirectory().catch(() => []),
      api.listBusinessScenarios(),
      api.listSkills(),
      api.listTestDatasets(),
      api.listTestRuns(),
      api.listUsers(),
      api.listModelProviders(),
      api.listModelProfiles(),
      api.getSalesforceModelCredentialMigrationStatus(),
      api.listAuditEvents(),
      api.listSalesforceGrants(),
      api.getSystemSettings(),
      api.getRuntimeOverview(),
      api.getAssistantProfile(),
      api.getAdminAssistantPolicy()
    ]);
    setData({ connection, companyProfile, knowledgeCapabilities, analysisRuns, schemaFingerprints, scopes, snapshots, diffs, candidates, objectCapabilities, objectDirectory, businessScenarios, skills, testDatasets, testRuns, users, providers, profiles, modelCredentialMigration, auditEvents, grants, systemStatus, runtimeOverview, assistantProfile, assistantPolicy });
  }, []);

  const bootstrap = useCallback(async (developmentLogin = false) => {
    setLoading(true);
    setError("");
    try {
      if (developmentLogin) await api.createDevSession();
      else {
        let resumed = false;
        let lastError: unknown;
        const resumeChannels: Array<"development" | "admin"> = isLocalDevelopment
          ? ["development", "admin"]
          : ["admin"];
        for (const channel of resumeChannels) {
          try {
            await api.resumeSession(channel);
            resumed = true;
            break;
          } catch (cause) {
            lastError = cause;
          }
        }
        if (!resumed && isLocalDevelopment) {
          try {
            await api.createDevSession();
            resumed = true;
          } catch {
            // The API keeps this route disabled unless development bypass is explicitly enabled.
          }
        }
        if (!resumed) throw lastError ?? new Error("未登录");
      }
      const nextIdentity = await api.getAdminIdentity();
      setIdentity(nextIdentity);
      await loadData(nextIdentity.user);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "管理中心加载失败");
    } finally {
      setLoading(false);
    }
  }, [loadData]);

  useEffect(() => {
    if (!salesforceBridge.embedded || salesforceBridge.connected) void bootstrap();
  }, [bootstrap, salesforceBridge.connected, salesforceBridge.embedded]);

  const runAction = useCallback(async (name: string, action: () => Promise<string>) => {
    setBusy(name);
    setNotice("");
    setError("");
    setActionFeedback({ name, status: "running", message: "" });
    try {
      const message = await action();
      setNotice(message);
      setActionFeedback({ name, status: "success", message });
      if (identity) await loadData(identity.user);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "操作失败";
      setError(message);
      setActionFeedback({ name, status: "error", message });
      if (identity) {
        try {
          await loadData(identity.user);
        } catch {
          // Keep the original action error visible when state recovery also fails.
        }
      }
    } finally {
      setBusy("");
    }
  }, [identity, loadData]);

  const updateAttachmentsEnabled = useCallback((attachmentsEnabled: boolean) => {
    setData((current) => ({
      ...current,
      assistantPolicy: current.assistantPolicy
        ? { ...current.assistantPolicy, attachmentsEnabled }
        : current.assistantPolicy
    }));
    void runAction("assistant-policy", async () => {
      const saved = await api.updateAssistantPolicy({ attachmentsEnabled });
      return saved.attachmentsEnabled
        ? "附件上传已开启。"
        : "附件上传已关闭；历史附件摘要仍可查看。";
    });
  }, [runAction]);

  const updateDataUsePolicy = useCallback((changes: Partial<Pick<AssistantPolicy, "attachmentBatchWritesEnabled" | "resultExport">>) => {
    setData((current) => ({
      ...current,
      assistantPolicy: current.assistantPolicy ? { ...current.assistantPolicy, ...changes } : current.assistantPolicy
    }));
    void runAction("data-use-policy", async () => {
      await api.updateAssistantPolicy(changes);
      return "数据使用策略已更新，并将同时用于 Salesforce 嵌入端和独立 Web 端。";
    });
  }, [runAction]);

  if (salesforceBridge.embedded && !salesforceBridge.connected) {
    if (salesforceBridge.error) return <main className="admin-auth-state"><h1>无法打开管理中心</h1><p>{salesforceBridge.error}</p><button className="button button--primary" type="button" onClick={() => window.location.reload()}>重新验证</button></main>;
    return <main className="admin-auth-state"><span className="admin-spinner" /><h1>正在验证管理权限</h1><p>正在通过 Salesforce 短时签名上下文建立管理会话。</p></main>;
  }
  if (loading) return <main className="admin-auth-state"><span className="admin-spinner" /><h1>正在验证管理权限</h1><p>正在读取当前用户、组织连接和产品配置。</p></main>;
  if (!identity && salesforceBridge.embedded) return <main className="admin-auth-state"><h1>无法打开管理中心</h1><p>{salesforceBridge.error || error || "当前 Salesforce 用户没有管理权限。"}</p><button className="button button--primary" type="button" onClick={() => window.location.reload()}>重新验证</button></main>;
  if (!identity) return <AdminLogin error={error} onAuthenticated={() => bootstrap()} />;

  const canPlatformAdmin = accountTypeOf(identity.user) === "admin";
  const canManageSkills = canPlatformAdmin;
  const managedBusinessScenarios = data.businessScenarios.filter((scenario) => scenario.key !== SYSTEM_BASELINE_SCENARIO_KEY);
  const availableSections: AdminSection[] = ["overview", "capabilities", "access", "audit", "settings"];
  const activeSection = availableSections.includes(section) ? section : "overview";

  return (
    <main className="admin-shell admin-shell--product">
      <AdminNavigation
        section={activeSection}
        sections={availableSections}
        user={identity.user}
        onChange={setSection}
        onLogout={async () => {
          await api.logout();
          if (salesforceBridge.embedded) {
            window.location.reload();
            return;
          }
          setIdentity(undefined);
          setData(emptyData);
          setError("");
        }}
      />
      <section className="admin-main">
        <header className="admin-topbar">
          <div><p className="eyebrow">{identity.organizationId}</p><h1>{sectionLabels[activeSection]}</h1></div>
          <span className={data.connection?.status === "connected" ? "connection-pill connection-pill--ok" : "connection-pill connection-pill--warn"}>
            {data.connection?.status === "connected" ? "Org 管理连接正常" : "连接需要处理"}
          </span>
        </header>
        {notice ? <div className="success-banner"><CheckIcon size={17} />{notice}</div> : null}
        {error ? <div className="admin-error-banner">{error}</div> : null}
        <div className="admin-product-content">
          {activeSection === "overview" ? <OverviewPage
            data={data}
            embedded={salesforceBridge.embedded}
            refreshing={busy === "installation-refresh"}
            onRefresh={() => runAction("installation-refresh", async () => "安装后状态已重新验证。")}
            onNavigate={(nextSection, targetId) => {
              setSection(nextSection);
              window.setTimeout(() => document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
            }}
          /> : null}
          {activeSection === "capabilities" ? <div className="admin-page-stack">
            <ScenarioConfigurationPath data={data} onNavigate={(nextSection, targetId) => {
              setSection(nextSection);
              window.setTimeout(() => document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
            }} />
            <div id="org-analysis"><OrgAnalysisPage data={data} busy={busy} feedback={actionFeedback?.name === "scan" ? actionFeedback : undefined} runAction={runAction} canManageScope={canManageSkills} /></div>
            <div id="company-profile"><CompanyProfileForm
                profile={data.companyProfile}
                canManage={canManageSkills}
                busy={busy === "company-profile"}
                onSave={async (profile) => runAction("company-profile", async () => {
                  const saved = await api.saveCompanyProfile(profile);
                  return `公司业务档案版本 ${saved.version} 已保存。`;
                })}
              /></div>
            <div id="knowledge-capability"><CapabilityStatusPanel
                snapshots={data.knowledgeCapabilities}
                canManage={canManageSkills}
                busy={busy === "knowledge-capability"}
                onProbe={async () => runAction("knowledge-capability", async () => {
                  const result = await api.probeKnowledgeCapabilities();
                  return `Knowledge 检测完成：当前用户状态为 ${result.currentUser.status}。`;
                })}
              /></div>
            <div id="scenario-generator"><ScenarioStudioWizard
                profile={data.companyProfile}
                canManage={canManageSkills}
                onCompiled={async (scenarioName) => runAction("scenario-studio-refresh", async () =>
                  `${scenarioName} 已生成受控场景草稿；请继续运行自动测试并进入试用。`)}
                onContinue={() => document.getElementById("scenario-workspace")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              /></div>
            <div id="scenario-workspace"><BusinessScenarioLifecycle
              scenarios={managedBusinessScenarios}
              skills={data.skills}
              testRuns={data.testRuns}
              busy={busy}
              runAction={runAction}
              canManage={canManageSkills}
              onCreateScenario={() => document.getElementById("scenario-generator")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            /></div>
          </div> : null}
          {activeSection === "access" && canPlatformAdmin ? <div id="user-access"><UsersPage users={data.users} scenarios={managedBusinessScenarios} currentUserId={identity.user.id} busy={busy} runAction={runAction} /></div> : null}
          {activeSection === "audit" ? <AuditPage events={data.auditEvents} overview={data.runtimeOverview} /> : null}
          {activeSection === "settings" ? <div className="admin-page-stack">
            <div id="salesforce-connection"><ConnectionsPage data={data} busy={busy} runAction={runAction} canConfigure={canPlatformAdmin} /></div>
            <div id="model-configuration"><ModelsPage connection={data.connection} providers={data.providers} profiles={data.profiles} migration={data.modelCredentialMigration} busy={busy} feedback={actionFeedback?.name === "model-create" ? actionFeedback : undefined} runAction={runAction} /></div>
            {data.assistantProfile ? <AssistantProfileSettings
              profile={data.assistantProfile}
              busy={busy === "assistant-profile"}
              onSave={async (input) => runAction("assistant-profile", async () => {
                const saved = await api.updateAssistantProfile(input);
                return `助手“${saved.name}”的资料已更新，并将同步用于 Web 与 Salesforce 嵌入端。`;
              })}
            /> : null}
            {data.assistantPolicy ? <DataUsePolicySettings
              policy={data.assistantPolicy}
              scopes={data.scopes.filter((scope) => scope.enabled && scope.maintenanceState === "active")}
              disabled={!canPlatformAdmin || Boolean(busy)}
              onAttachmentsEnabled={updateAttachmentsEnabled}
              onChange={updateDataUsePolicy}
              onObjectExportPolicy={(scope, exportPolicy) => void runAction(`object-export-${scope.objectApiName}`, async () => {
                await api.updateObjectExportPolicy(scope.objectApiName, exportPolicy);
                return `${scope.objectApiName} 的导出策略已更新。`;
              })}
            /> : null}
            {canPlatformAdmin ? <div id="system-settings"><SystemSettingsPage status={data.systemStatus} /></div> : null}
          </div> : null}
        </div>
      </section>
    </main>
  );
}

function ScenarioConfigurationPath({ data, onNavigate }: {
  data: AdminData;
  onNavigate: (section: AdminSection, targetId: string) => void;
}) {
  const latestSnapshot = data.snapshots.find((snapshot) => snapshot.status === "completed");
  const latestTestRun = data.testRuns[0];
  const hasObjectScope = data.scopes.some((scope) => scope.enabled && scope.maintenanceState === "active");
  const managedScenarios = data.businessScenarios.filter((scenario) => scenario.key !== SYSTEM_BASELINE_SCENARIO_KEY);
  const systemBaseline = data.businessScenarios.find((scenario) => scenario.key === SYSTEM_BASELINE_SCENARIO_KEY);
  const hasDraft = managedScenarios.length > 0;
  const hasPassedTest = data.skills.some((skill) => assessReleaseEvidence(skill, data.testRuns).status === "current_passed");
  const hasHistoricalPassOnly = !hasPassedTest && data.testRuns.some((run) => run.status === "passed");
  const hasPublishedScenario = managedScenarios.some((scenario) => scenario.status === "published");
  const steps: Array<{
    number: number;
    label: string;
    description: string;
    complete: boolean;
    attention?: boolean;
    section: AdminSection;
    targetId: string;
  }> = [
    { number: 1, label: "连接与对象范围", description: data.connection?.status === "connected" && hasObjectScope ? `${data.scopes.filter((scope) => scope.enabled).length} 个对象已纳管` : "确认连接并选择允许扫描的对象", complete: data.connection?.status === "connected" && hasObjectScope, section: data.connection?.status === "connected" ? "capabilities" : "settings", targetId: data.connection?.status === "connected" ? "org-analysis" : "salesforce-connection" },
    { number: 2, label: "Org 分析与基础助手", description: systemBaseline?.status === "published" ? `${latestSnapshot?.objectCatalogs.length ?? 0} 个对象的基础操作已可用` : "扫描元数据并建立通用规划与执行能力", complete: Boolean(latestSnapshot && systemBaseline?.status === "published"), section: "capabilities", targetId: "org-analysis" },
    { number: 3, label: "公司业务档案", description: data.companyProfile ? `版本 ${data.companyProfile.version} 已生效` : "为专属场景提供业务语境", complete: Boolean(data.companyProfile), section: "capabilities", targetId: "company-profile" },
    { number: 4, label: "特殊场景草稿", description: hasDraft ? `${managedScenarios.length} 个特殊场景` : "仅为固定审批、专用映射、外部集成和可复用规则建场景", complete: hasDraft, section: "capabilities", targetId: "scenario-generator" },
    { number: 5, label: "自动测试与试用", description: hasPassedTest ? "当前冻结测试集已全部通过" : hasHistoricalPassOnly ? "仅有历史通过记录，请重新运行当前测试集" : latestTestRun?.status === "failed" ? "最近测试未通过，请处理模型或规则" : "使用真实模型验证结果", complete: hasPassedTest, attention: hasHistoricalPassOnly || latestTestRun?.status === "failed", section: latestTestRun?.status === "failed" ? "settings" : "capabilities", targetId: latestTestRun?.status === "failed" ? "model-configuration" : "scenario-workspace" },
    { number: 6, label: "发布与用户分配", description: hasPublishedScenario ? "专属场景已发布并可分配" : "测试通过后发布；可同步分配有效用户", complete: hasPublishedScenario, section: hasPublishedScenario ? "access" : "capabilities", targetId: hasPublishedScenario ? "user-access" : "scenario-workspace" }
  ];
  const nextStep = steps.find((step) => !step.complete);
  const completeCount = steps.filter((step) => step.complete).length;

  return <section className="admin-card configuration-path" aria-labelledby="configuration-path-title">
    <header className="configuration-path__header">
      <div><p className="eyebrow">产品配置路径</p><h2 id="configuration-path-title">从真实 Org 到可用场景</h2><p>按顺序完成配置。每一步都保留草稿和审计记录，只有测试通过的版本才能发布。</p></div>
      <div className="configuration-path__summary">
        <span><strong>{completeCount}</strong>/6 步已完成</span>
        {nextStep ? <button className="button button--primary" type="button" onClick={() => onNavigate(nextStep.section, nextStep.targetId)}>继续：{nextStep.label}</button> : <span className="connection-pill connection-pill--ok">配置已就绪</span>}
      </div>
    </header>
    <ol className="configuration-path__steps">
      {steps.map((step) => <li className={`configuration-step ${step.complete ? "configuration-step--complete" : ""} ${step.attention ? "configuration-step--attention" : ""}`} key={step.number}>
        <button type="button" aria-label={`配置步骤 ${step.number}：${step.label}`} aria-current={nextStep?.number === step.number ? "step" : undefined} onClick={() => onNavigate(step.section, step.targetId)}>
          <span className="configuration-step__number">{step.complete ? <CheckIcon size={14} /> : step.number}</span>
          <span><strong>{step.label}</strong><small>{step.description}</small></span>
        </button>
      </li>)}
    </ol>
  </section>;
}

function AdminLogin(props: { error: string; onAuthenticated: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState("");
  const displayError = localError || (props.error === "未登录" ? "" : props.error);
  const startSso = async () => {
    setBusy(true);
    setLocalError("");
    try {
      const result = await api.startSalesforceSso("/admin");
      window.location.assign(result.authorizationUrl);
    } catch (cause) {
      setLocalError(cause instanceof Error ? cause.message : "Salesforce 企业登录不可用");
      setBusy(false);
    }
  };
  return <main className="admin-auth-state admin-auth-state--login"><section className="admin-login-card">
    <div className="admin-login-brand"><SparklesIcon /><div><h1>Salesforce AI 助手</h1><p>管理中心</p></div></div>
    <h2>Salesforce 管理员登录</h2>
    <p className="muted">使用已经完成产品激活和管理员绑定的 Salesforce 账号登录。</p>
    {displayError ? <div className="admin-error-banner">{displayError}</div> : null}
    <button className="button button--primary button--full" type="button" disabled={busy} onClick={() => void startSso()}>{busy ? "正在连接 Salesforce…" : "使用 Salesforce 管理员登录"}</button>
    <div className="admin-login-help"><strong>首次启用当前 Salesforce 组织？</strong><p>请先在 Salesforce 中打开“AI 助手设置”，输入产品激活码并完成首位管理员授权。安装产品包不会自动开通服务。</p></div>
  </section></main>;
}

function AdminNavigation(props: {
  section: AdminSection;
  sections: AdminSection[];
  user: PlatformUser;
  onChange: (section: AdminSection) => void;
  onLogout: () => Promise<void>;
}) {
  return <aside className="admin-nav">
    <div className="admin-brand"><span><SparklesIcon /></span><div><strong>Salesforce AI 助手</strong><small>管理中心</small></div></div>
    <nav aria-label="管理导航">
      {props.sections.map((section) => <button type="button" key={section} className={props.section === section ? "active" : ""} onClick={() => props.onChange(section)}>
        {section === "capabilities" ? <LayersIcon /> : section === "audit" ? <HistoryIcon /> : <SettingsIcon />}{sectionLabels[section]}
      </button>)}
    </nav>
    <div className="admin-user">
      <strong>{props.user.displayName}</strong>
      <span>{accountTypeLabels[accountTypeOf(props.user)]}</span>
      <button type="button" onClick={() => void props.onLogout()}>退出管理中心</button>
    </div>
  </aside>;
}

function OverviewPage({ data, embedded, refreshing, onRefresh, onNavigate }: {
  data: AdminData;
  embedded: boolean;
  refreshing: boolean;
  onRefresh: () => Promise<void>;
  onNavigate: (section: AdminSection, targetId: string) => void;
}) {
  const latestSnapshot = data.snapshots[0];
  const latestRun = data.testRuns[0];
  const managedScenarios = data.businessScenarios.filter((scenario) => scenario.key !== SYSTEM_BASELINE_SCENARIO_KEY);
  const published = managedScenarios.filter((scenario) => scenario.status === "published").length;
  const systemBaselineReady = data.businessScenarios.some((scenario) => scenario.key === SYSTEM_BASELINE_SCENARIO_KEY && scenario.status === "published");
  const blockingChanges = data.diffs[0]?.changes.filter((change) => change.severity === "blocking" || change.severity === "high_risk").length ?? 0;
  return <div className="admin-page-stack">
    <section className="metric-grid">
      <MetricCard label="扫描对象" value={String(data.scopes.filter((scope) => scope.enabled && scope.maintenanceState === "active").length)} detail="仅管理员选择的范围" />
      <MetricCard label="系统基础能力" value={systemBaselineReady ? "已就绪" : "待分析"} detail="按对象配置读取、创建、更新和单记录删除" tone={systemBaselineReady ? "normal" : "warning"} />
      <MetricCard label="已发布专属场景" value={String(published)} detail={`${managedScenarios.length - published} 个待测试、试用或已停用`} />
      <MetricCard label="最新测试" value={latestRun ? `${latestRun.passed}/${latestRun.passed + latestRun.failed}` : "未运行"} detail={latestRun?.status === "passed" ? "全部通过" : "需要验证"} />
      <MetricCard label="阻断变化" value={String(blockingChanges)} detail={blockingChanges ? "发布前必须处理" : "当前无阻断项"} tone={blockingChanges ? "warning" : "normal"} />
    </section>
    <section className="admin-card"><h2>当前产品状态</h2><div className="summary-list">
      <SummaryRow label="Org 管理连接" value={data.systemStatus?.salesforce.orgManagementConfigured ? `${data.systemStatus.salesforce.orgManagementMode} · 只读` : "尚未授权"} />
      <SummaryRow label="最近扫描" value={latestSnapshot ? `${formatTime(latestSnapshot.completedAt ?? latestSnapshot.createdAt)} · ${latestSnapshot.objectCatalogs.length} 个对象` : "尚未运行"} />
      <SummaryRow label="对象能力" value={`${data.objectCapabilities.filter((capability) => capability.ready).length} 个已就绪；${data.candidates.filter((candidate) => candidate.status === "suggested").length} 个候选待审核`} />
      <SummaryRow label="默认模型" value={data.profiles.find((profile) => profile.enabled && profile.isDefault)?.model ?? "尚未配置平台模型"} />
    </div></section>
    <PostInstallGuide data={data} embedded={embedded} refreshing={refreshing} onRefresh={onRefresh} onNavigate={onNavigate} />
  </div>;
}

function PostInstallGuide({ data, embedded, refreshing, onRefresh, onNavigate }: {
  data: AdminData;
  embedded: boolean;
  refreshing: boolean;
  onRefresh: () => Promise<void>;
  onNavigate: (section: AdminSection, targetId: string) => void;
}) {
  const activeScopes = data.scopes.filter((scope) => scope.enabled && scope.maintenanceState === "active");
  const latestSnapshot = data.snapshots.find((snapshot) => snapshot.status === "completed");
  const systemBaselineReady = data.businessScenarios.some((scenario) =>
    scenario.key === SYSTEM_BASELINE_SCENARIO_KEY && scenario.status === "published");
  const managedScenarios = data.businessScenarios.filter((scenario) => scenario.key !== SYSTEM_BASELINE_SCENARIO_KEY);
  const publishedManagedScenarios = managedScenarios.filter((scenario) => scenario.status === "published");
  const managedSkillKeys = new Set(managedScenarios.map((scenario) => scenario.linkedSkillKey));
  const currentManagedEvidence = data.skills.some((skill) =>
    managedSkillKeys.has(skill.key) && assessReleaseEvidence(skill, data.testRuns).status === "current_passed");
  const verifiedProviderIds = new Set(data.providers
    .filter((provider) => provider.status === "verified")
    .map((provider) => provider.id));
  const mainModelReady = data.profiles.some((profile) =>
    profile.enabled && profile.purpose === "assistant" &&
    (profile.role === "main_reasoning" || (!profile.role && profile.isDefault)) &&
    verifiedProviderIds.has(profile.providerId));
  const activeOrdinaryUsers = data.systemStatus?.userAccess.activeOrdinaryUsers ?? 0;
  const readyOrdinaryUsers = data.systemStatus?.userAccess.readyOrdinaryUsers ?? 0;
  const runtimeReady = Boolean(
    data.systemStatus?.salesforce.ticketVerificationConfigured && data.systemStatus.credentialVault.configured
  );
  const hasActiveUserAccessGrant = data.grants.some((grant) =>
    grant.purpose === "business_data" && grant.status === "active" &&
    (!grant.expiresAt || Date.parse(grant.expiresAt) > Date.now()));
  const userEcaReady = Boolean(data.systemStatus?.salesforce.userAccessOAuthConfigured && hasActiveUserAccessGrant);
  const hasActiveManagementGrant = data.grants.some((grant) =>
    grant.purpose === "org_management" && grant.status === "active" &&
    (!grant.expiresAt || Date.parse(grant.expiresAt) > Date.now()));
  const managementReady = Boolean(
    data.systemStatus?.salesforce.orgManagementConfigured &&
    data.systemStatus.salesforce.orgManagementMode === "admin_oauth" &&
    hasActiveManagementGrant
  );
  const objectScopeReady = Boolean(activeScopes.length && latestSnapshot && systemBaselineReady);
  const customReleaseRequired = managedScenarios.length > 0;
  const customReleaseReady = !customReleaseRequired || Boolean(publishedManagedScenarios.length && currentManagedEvidence);
  const steps: Array<{
    key: string;
    label: string;
    detail: string;
    path: string;
    complete: boolean;
    optional?: boolean;
    section?: AdminSection;
    targetId?: string;
  }> = [
    {
      key: "activation", label: "1. 产品激活",
      detail: data.systemStatus ? `Org ${data.systemStatus.organizationId} 已建立租户上下文` : "请由激活码指定的首位管理员完成激活",
      path: "Salesforce 应用启动器 → AI 助手设置 → 验证激活码", complete: Boolean(data.systemStatus)
    },
    {
      key: "runtime", label: "2. Runtime 连接",
      detail: runtimeReady ? `Runtime ${data.systemStatus?.runtimeVersion} 可达，签名验证和凭据库已就绪` : "Runtime 必须同时具备 Salesforce 票据验证和加密凭据库",
      path: "AI 助手设置 → 连接设置 → 平台服务地址", complete: runtimeReady,
      section: "settings", targetId: "system-settings"
    },
    {
      key: "certificate", label: "3. 签名证书",
      detail: embedded ? "当前会话已通过 Apex 签发的短时票据建立" : "独立 Web 无法代替 Salesforce 验证证书，请回到设置 LWC 检查",
      path: "Salesforce 设置 → 证书和密钥管理 → AI_Agent_Signing", complete: embedded
    },
    {
      key: "csp-eca", label: "4. CSP / ECA",
      detail: embedded && userEcaReady ? "Salesforce 内嵌连接已建立，且 User Access ECA 已有实际 OAuth 授权证据" : "需同时允许 Runtime 内嵌域名，并完成一次 User Access ECA OAuth",
      path: "Salesforce 设置 → CSP 受信任站点；外部客户端应用管理器 → User Access ECA",
      complete: embedded && userEcaReady, section: "settings", targetId: "system-settings"
    },
    {
      key: "admin-oauth", label: "5. 管理员连接",
      detail: managementReady ? `${data.systemStatus?.salesforce.orgManagementMode} 已建立` : "用当前 Salesforce 管理员建立 Org 管理 OAuth",
      path: "管理中心 → 系统设置 → 连接当前 Salesforce Org",
      complete: managementReady, section: "settings", targetId: "salesforce-connection"
    },
    {
      key: "model", label: "6. 主推理模型",
      detail: mainModelReady ? "已有启用且通过能力探测的主推理模型" : "模型密钥只能保存到当前租户 Runtime Vault",
      path: "管理中心 → 系统设置 → 模型配置 → 验证并保存",
      complete: mainModelReady, section: "settings", targetId: "model-configuration"
    },
    {
      key: "storage", label: "7. 存储 / 语音",
      detail: data.systemStatus?.attachmentStorage.productionReady
        ? `附件存储 ${data.systemStatus.attachmentStorage.kind} 已就绪；语音为可选能力`
        : "正式环境请配置 OSS/S3；语音转写可按需开启",
      path: "管理中心 → 系统设置 → 运行环境；Runtime SecretProvider",
      complete: Boolean(data.systemStatus?.attachmentStorage.productionReady), optional: true,
      section: "settings", targetId: "system-settings"
    },
    {
      key: "objects", label: "8. 对象范围",
      detail: objectScopeReady ? `${activeScopes.length} 个对象已编译系统基础能力` : "选择允许范围，完成 Org 分析并发布基础能力快照",
      path: "管理中心 → 能力中心 → 对象范围 / 开始分析",
      complete: objectScopeReady, section: "capabilities", targetId: "org-analysis"
    },
    {
      key: "users", label: "9. 用户与权限",
      detail: readyOrdinaryUsers ? `${readyOrdinaryUsers}/${activeOrdinaryUsers} 位普通 AI 用户的绑定与 entitlement 当前有效` : "至少配置一位普通用户的 Salesforce 绑定与 AI 资格",
      path: "管理中心 → 用户与权限 → AI 访问资格与场景",
      complete: readyOrdinaryUsers > 0, section: "access", targetId: "user-access"
    },
    {
      key: "release", label: "10. 测试与发布",
      detail: customReleaseRequired
        ? customReleaseReady
          ? `${publishedManagedScenarios.length} 个专属场景已具备当前冻结集通过证据`
          : "专属场景必须通过当前冻结测试集、试用后才能发布"
        : "当前只使用系统基础能力；专属场景测试为可选",
      path: "管理中心 → 能力中心 → 自动测试 → 试用 → 正式发布",
      complete: customReleaseReady, optional: !customReleaseRequired,
      section: "capabilities", targetId: "scenario-workspace"
    }
  ];
  const requiredSteps = steps.filter((step) => !step.optional);
  const completeRequiredSteps = requiredSteps.filter((step) => step.complete).length;

  return <section className="admin-card post-install-guide" aria-labelledby="post-install-guide-title">
    <header className="post-install-guide__header">
      <div><p className="eyebrow">安装后向导</p><h2 id="post-install-guide-title">从激活到可发布</h2><p>状态来自当前 Runtime 和已发布配置；不能自动完成的 Salesforce 步骤保留精确设置路径。</p></div>
      <div className="post-install-guide__summary"><span><strong>{completeRequiredSteps}</strong>/{requiredSteps.length} 个必需项已验证</span><button className="button button--secondary" type="button" disabled={refreshing} onClick={() => void onRefresh()}>{refreshing ? "正在验证…" : "重新验证全部"}</button></div>
    </header>
    <ol className="post-install-guide__steps">{steps.map((step) => <li key={step.key} className={step.complete ? "is-complete" : step.optional ? "is-optional" : "is-required"}>
      <span className="post-install-guide__status">{step.complete ? <CheckIcon size={14} /> : step.optional ? "可选" : "待处理"}</span>
      <div><strong>{step.label}</strong><p>{step.detail}</p><small>{step.path}</small></div>
      {step.section && step.targetId
        ? <button className="text-button" type="button" onClick={() => onNavigate(step.section!, step.targetId!)}>{step.complete ? "查看" : "去配置"}</button>
        : !step.complete ? <button className="text-button" type="button" onClick={() => void onRefresh()}>重新验证</button> : null}
    </li>)}</ol>
  </section>;
}

function MetricCard({ label, value, detail, tone = "normal" }: { label: string; value: string; detail: string; tone?: "normal" | "warning" }) {
  return <article className={`metric-card ${tone === "warning" ? "metric-card--warning" : ""}`}><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return <div className="summary-row"><span>{label}</span><strong>{value}</strong></div>;
}

function ConnectionsPage({ data, busy, runAction, canConfigure }: { data: AdminData; busy: string; runAction: (name: string, action: () => Promise<string>) => Promise<void>; canConfigure: boolean }) {
  const managementGrant = data.grants.find((grant) => grant.purpose === "org_management" && grant.status === "active");
  const businessGrant = data.grants.find((grant) => grant.purpose === "business_data" && grant.status === "active");
  const beginManagementGrant = async () => {
    const authorization = await api.startSalesforceGrant("org_management", "/admin#salesforce-connection");
    window.location.assign(authorization.authorizationUrl);
  };
  return <div className="admin-page-stack">
    <section className="admin-card admin-card--header"><div><h2>Salesforce 连接</h2><p>{data.connection ? `${data.connection.displayName} · ${data.connection.cloudVariant === "alibaba_cloud" ? "Salesforce on Alibaba Cloud" : "Salesforce Global"}` : "尚未建立组织连接"}</p><small className="muted">{data.connection?.lastVerifiedAt ? `上次验证：${formatTime(data.connection.lastVerifiedAt)} · REST ${data.connection.apiVersion ?? "待协商"}` : data.connection?.lastError ?? "请从 Salesforce 的“AI 助手设置”完成首次激活"}</small></div>{canConfigure ? <button className="button button--secondary" disabled={Boolean(busy) || !data.connection} type="button" onClick={() => void runAction("verify-org", async () => { const result = await api.verifyOrgConnection(); return `连接验证通过：${result.organization.name}${result.organization.instanceName ? `（${result.organization.instanceName}）` : ""}${result.organization.isSandbox ? " · Sandbox" : ""}。`; })}>{busy === "verify-org" ? "验证中…" : "验证连接"}</button> : <span className={data.connection?.status === "connected" ? "connection-pill connection-pill--ok" : "connection-pill connection-pill--warn"}>{data.connection?.status === "connected" ? "连接正常" : "需要处理"}</span>}</section>
    <section className="admin-card"><h2>自动识别的组织环境</h2><p className="muted">以下信息来自首次激活的 Salesforce 签名上下文和 OAuth 回调，不需要在管理中心重复维护。</p><div className="summary-list">
      <SummaryRow label="环境" value={data.connection ? `${data.connection.cloudVariant === "alibaba_cloud" ? "Salesforce on Alibaba Cloud" : "Salesforce Global"}${data.connection.isSandbox ? " · 沙盒" : " · 正式/Developer"}` : "等待首次激活"} />
      <SummaryRow label="登录源" value={data.connection?.loginUrl ?? "等待 Salesforce 自动识别"} />
      <SummaryRow label="实例域名" value={data.connection?.instanceUrl ?? data.connection?.myDomainUrl ?? "授权后自动登记"} />
      <SummaryRow label="Org ID" value={data.connection?.organizationId ?? "等待首次激活"} />
    </div><div className="admin-info-banner">如识别结果不正确，请回到 Salesforce“AI 助手设置”重新授权；不要手工猜测登录域或实例地址。</div></section>
    <section className="admin-card"><h2>当前用户权限策略</h2><p className="muted">AI 助手权限集只负责显示入口、签发短时身份票据和调用受控网关，不授予潜在客户、客户、联系人、业务机会等业务对象权限。</p><div className="admin-info-banner">Org 管理授权只用于对象目录、元数据扫描、用户目录和健康检查，且服务端强制只读。业务数据查询和写入始终使用当前普通用户本人的 CRUD、FLS、共享规则与记录权限。</div></section>
    <section className="admin-card"><h2>Salesforce 授权</h2><p className="muted">激活管理员默认提供只读 Org 管理授权；独立 Integration User 只是无人值守或严格职责隔离场景的可选配置。</p><div className="grant-grid">
      <article><strong>Org 管理授权</strong><span>{managementGrant ? `已由当前管理员授权 · ${managementGrant.salesforceUserId}` : data.systemStatus?.salesforce.orgManagementConfigured ? `组织级管理连接已就绪 · ${data.systemStatus.salesforce.orgManagementMode}` : "尚未授权，无法执行扫描和健康检查"}</span>{canConfigure ? <div className="editor-actions"><button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => void beginManagementGrant()}>{managementGrant ? "重新授权" : "管理员授权"}</button>{managementGrant ? <button className="text-button" type="button" disabled={Boolean(busy)} onClick={() => void runAction("revoke-management-grant", async () => { await api.revokeSalesforceGrant(managementGrant.id); return "Org 管理授权已撤销；如无可选 Integration User，扫描将暂停。"; })}>撤销</button> : null}</div> : null}</article>
      <article><strong>当前管理员本人业务授权（可选）</strong><span>{businessGrant ? `已连接用户 ${businessGrant.salesforceUserId}` : "仅当当前管理员本人也使用助手时需要；普通用户在助手内自助连接"}</span><div className="editor-actions"><button className="button button--secondary" type="button" onClick={() => window.location.assign("/workspace")}>{businessGrant ? "去工作台检查或重连" : "去工作台自助连接"}</button>{businessGrant ? <button className="text-button" type="button" disabled={Boolean(busy)} onClick={() => void runAction("revoke-business-grant", async () => { await api.revokeSalesforceGrant(businessGrant.id); return "当前管理员本人的业务数据授权已撤销。"; })}>撤销</button> : null}</div></article>
    </div></section>
  </div>;
}

function DataUsePolicySettings({ policy, scopes, disabled, onAttachmentsEnabled, onChange, onObjectExportPolicy }: {
  policy: Pick<AssistantPolicy, "attachmentsEnabled" | "attachmentBatchWritesEnabled" | "resultExport">;
  scopes: OrgScanScope[];
  disabled: boolean;
  onAttachmentsEnabled: (enabled: boolean) => void;
  onChange: (changes: Partial<Pick<AssistantPolicy, "attachmentBatchWritesEnabled" | "resultExport">>) => void;
  onObjectExportPolicy: (scope: OrgScanScope, policy: "inherit" | "allow" | "deny") => void;
}) {
  const resultExport = policy.resultExport ?? {
    enabled: true,
    detailFormats: ["excel", "csv"] as const,
    groupedFormats: ["excel", "csv"] as const,
    chartFormats: ["png", "svg", "excel", "csv"] as const,
    maxRecords: DEFAULT_RESULT_EXPORT_MAX_RECORDS
  };
  const updateFormats = (key: "detailFormats" | "groupedFormats" | "chartFormats", format: "excel" | "csv" | "png" | "svg", checked: boolean) => {
    const next = checked ? [...new Set([...resultExport[key], format])] : resultExport[key].filter((value) => value !== format);
    if (!next.length) return;
    onChange({ resultExport: { ...resultExport, [key]: next } });
  };
  return <section className="admin-card data-use-policy" id="data-use-policy">
    <div className="admin-card--header"><div><h2>数据使用策略</h2><p className="muted">附件是输入策略，结果导出是输出策略。两者分开管理，但都遵守当前 Salesforce 用户权限。</p></div></div>
    <div className="data-use-policy__grid">
      <article><h3>附件输入</h3><label className="policy-switch"><input type="checkbox" aria-label="允许附件上传" checked={policy.attachmentsEnabled} disabled={disabled} onChange={(event) => onAttachmentsEnabled(event.currentTarget.checked)} /><span>{policy.attachmentsEnabled ? "允许上传" : "已关闭"}</span></label><label className="policy-switch"><input type="checkbox" aria-label="允许表格生成批量写入预览" checked={policy.attachmentBatchWritesEnabled ?? true} disabled={disabled || !policy.attachmentsEnabled} onChange={(event) => onChange({ attachmentBatchWritesEnabled: event.currentTarget.checked })} /><span>允许表格生成批量写入预览</span></label><p className="muted">每条最多 3 个，单个 20MB，总量 40MB，支持粘贴图片或文件，默认保留 24 小时。</p></article>
      <article><h3>结果导出</h3><label className="policy-switch"><input type="checkbox" aria-label="允许结果导出" checked={resultExport.enabled} disabled={disabled} onChange={(event) => onChange({ resultExport: { ...resultExport, enabled: event.currentTarget.checked } })} /><span>{resultExport.enabled ? "允许导出" : "已关闭"}</span></label><label>单次上限<input type="number" min={1} max={MAX_RESULT_EXPORT_RECORDS} value={resultExport.maxRecords} disabled={disabled || !resultExport.enabled} onChange={(event) => onChange({ resultExport: { ...resultExport, maxRecords: Math.max(1, Math.min(MAX_RESULT_EXPORT_RECORDS, Number(event.currentTarget.value) || 1)) } })} /></label><div className="export-format-grid">
        <strong>明细列表</strong>{(["excel", "csv"] as const).map((format) => <label key={`detail-${format}`}><input type="checkbox" checked={resultExport.detailFormats.includes(format)} disabled={disabled || !resultExport.enabled} onChange={(event) => updateFormats("detailFormats", format, event.currentTarget.checked)} />{format.toUpperCase()}</label>)}
        <strong>分组结果</strong>{(["excel", "csv"] as const).map((format) => <label key={`grouped-${format}`}><input type="checkbox" checked={resultExport.groupedFormats.includes(format)} disabled={disabled || !resultExport.enabled} onChange={(event) => updateFormats("groupedFormats", format, event.currentTarget.checked)} />{format.toUpperCase()}</label>)}
        <strong>图表</strong>{(["png", "svg", "excel", "csv"] as const).map((format) => <label key={`chart-${format}`}><input type="checkbox" checked={resultExport.chartFormats.includes(format)} disabled={disabled || !resultExport.enabled} onChange={(event) => updateFormats("chartFormats", format, event.currentTarget.checked)} />{format.toUpperCase()}</label>)}
      </div><p className="muted">默认及最高单次 2000 条；普通查询仍按需返回，只有“全部、完整列表、导出”或明确数量才扩大到该上限。单一 KPI 和空结果不显示下载。</p></article>
    </div>
    {scopes.length ? <div className="object-export-policy"><h3>对象级例外</h3><p className="muted">默认继承组织策略；“允许”仍不能绕过组织关闭或当前用户权限，“禁止”可进一步收紧。</p><div className="object-export-policy__list">{scopes.map((scope) => <label key={scope.objectApiName}><span>{scope.objectApiName}</span><select value={scope.exportPolicy ?? "inherit"} disabled={disabled} onChange={(event) => onObjectExportPolicy(scope, event.currentTarget.value as "inherit" | "allow" | "deny")}><option value="inherit">继承组织策略</option><option value="allow">允许</option><option value="deny">禁止</option></select></label>)}</div></div> : null}
  </section>;
}

function OrgAnalysisPage({ data, busy, feedback, runAction, canManageScope }: { data: AdminData; busy: string; feedback?: ActionFeedback; runAction: (name: string, action: () => Promise<string>) => Promise<void>; canManageScope: boolean }) {
  const [draftSelections, setDraftSelections] = useState<Record<string, OrgScanScope["operations"]>>(() => effectiveSelectionDraft(data));
  useEffect(() => {
    setDraftSelections(effectiveSelectionDraft(data));
  }, [data.scopes, data.objectDirectory]);
  const activeScopes = data.scopes.filter((scope) => scope.enabled && scope.maintenanceState === "active");
  const selectedObjectApiNames = new Set(Object.keys(draftSelections).filter((objectApiName) => draftSelections[objectApiName]?.length));
  const selectedObjectCount = selectedObjectApiNames.size;
  const activeSelectionSignature = selectionSignature(Object.fromEntries(activeScopes.map((scope) => [scope.objectApiName, scope.operations])));
  const draftSelectionSignature = selectionSignature(draftSelections);
  const hasUnsavedSelections = activeSelectionSignature !== draftSelectionSignature;
  const latestSnapshot = data.snapshots[0];
  const scanButtonLabel = busy === "scan"
    ? "正在分析…"
    : latestSnapshot
      ? hasUnsavedSelections ? "保存并分析" : "重新分析"
      : hasUnsavedSelections ? "保存并分析" : "开始分析";
  const blockingWarnings = latestSnapshot?.scanWarnings?.filter(isBlockingScanWarning) ?? [];
  const informationalNotices = latestSnapshot?.scanWarnings?.filter((warning) => !isBlockingScanWarning(warning)) ?? [];
  const systemBaseline = data.businessScenarios.find((scenario) => scenario.key === SYSTEM_BASELINE_SCENARIO_KEY);
  const saveSelections = async () => {
    const selectedObjects = new Set(Object.keys(draftSelections).filter((objectApiName) => draftSelections[objectApiName]?.length));
    if (!selectedObjects.size) throw new Error("请至少选择 1 个 Salesforce 对象");
    if (selectedObjects.size > MAX_ORG_SCAN_OBJECTS) throw new Error(`组织扫描范围最多包含 ${MAX_ORG_SCAN_OBJECTS} 个底层对象`);
    await api.saveOrgSelections([...selectedObjects].map((objectApiName) => ({
      objectApiName,
      operations: draftSelections[objectApiName]?.length ? draftSelections[objectApiName] : ["query"],
      permissionSetApiNames: []
    })));
    return `已保存 ${selectedObjects.size} 个对象及其基础操作；只有这些对象会进入组织级分析。`;
  };
  return <div className="admin-page-stack">
    <section className="admin-card"><div className="admin-card--header"><div><h2>AI 助手扫描范围</h2><p>初始范围为空。请从当前 Org 对象目录选择最多 {MAX_ORG_SCAN_OBJECTS} 个对象，并逐项开启所需基础操作。</p></div>{canManageScope ? <button className="button button--primary" disabled={Boolean(busy) || selectedObjectCount === 0} type="button" onClick={() => void runAction("scan", async () => {
      if (hasUnsavedSelections) await saveSelections();
      const result = await api.runOrgScan();
      return `分析完成：${result.snapshot.objectCatalogs.length} 个底层对象，生成 ${result.candidates.length} 个能力建议。`;
    })}>{scanButtonLabel}</button> : <span className="muted">只读查看</span>}</div>{busy === "scan" ? <div className="admin-info-banner" role="status">正在读取 Salesforce 元数据并生成系统基础能力，通常需要 10–60 秒，请勿重复点击。</div> : feedback?.status === "error" ? <div className="admin-error-banner" role="alert">分析失败：{feedback.message}</div> : feedback?.status === "success" ? <div className="success-banner"><CheckIcon size={17} />{feedback.message}</div> : null}</section>
    <section className="admin-card">
      <div className="scope-selection-summary"><strong>当前待保存：{selectedObjectCount} 个对象</strong><span>新增对象默认只开启读取；创建、更新和单记录删除必须由管理员逐项开启。</span></div>
      {data.objectDirectory.length ? <OrgObjectSelector
        directory={data.objectDirectory}
        selections={draftSelections}
        maxSelections={MAX_ORG_SCAN_OBJECTS}
        disabled={!canManageScope || Boolean(busy)}
        onChange={setDraftSelections}
      /> : <p className="empty-copy">{data.connection ? "未读取到对象目录，请先完成管理员 Org 管理授权并验证连接。" : "请先从 Salesforce 完成首次激活。"}</p>}
      {canManageScope ? <div className="editor-actions"><button className="button button--primary" type="button" disabled={Boolean(busy) || selectedObjectCount === 0 || selectedObjectCount > MAX_ORG_SCAN_OBJECTS || !hasUnsavedSelections} onClick={() => void runAction("object-selections", saveSelections)}>保存扫描范围</button><span className="muted">{hasUnsavedSelections ? "有尚未保存的调整" : "当前选择已生效"}；当前生效 {activeScopes.length} 个底层对象。</span></div> : null}
    </section>
    <section className="admin-card baseline-capability-card">
      <div><p className="eyebrow">系统基础能力</p><h2>通用规划与执行</h2><p>扫描成功后，按已选对象的基础操作提供读取、创建、更新和单记录删除。固定审批、专用字段映射、外部集成和可复用规则才作为专属能力管理。</p></div>
      <span className={systemBaseline?.status === "published" ? "connection-pill connection-pill--ok" : "connection-pill connection-pill--warn"}>{systemBaseline?.status === "published" ? "已启用" : latestSnapshot ? "需要重新分析" : "等待首次分析"}</span>
    </section>
    <section className="admin-card"><h2>对象就绪状态</h2><p className="muted">系统基础能力自动覆盖已选操作。只有固定审批、专用映射、外部集成或可复用规则，才需要准备专属场景能力。</p>
      {data.candidates.filter((candidate) => candidate.status === "suggested").length ? <div className="candidate-list">{data.candidates.filter((candidate) => candidate.status === "suggested").map((candidate) => <article key={candidate.id} className="candidate-row"><div><strong>{candidate.name}</strong><p>{candidate.description}</p><small>{candidate.objectApiName} · {candidate.recommendedOperations.join(" / ")} · {candidate.risks.length} 项风险提示</small></div>{canManageScope ? <button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => void runAction(`candidate-${candidate.id}`, async () => { const skill = await api.acceptSkillCandidate(candidate.id); return `${skill.name} 的对象能力草稿已生成；请完成测试、试用和发布后用于特殊场景。`; })}>准备专属场景能力</button> : null}</article>)}</div> : <p className="empty-copy">暂无待处理候选。已选对象的系统基础能力不需要逐个发布。</p>}
      {data.skills.filter((skill) => skill.key.startsWith("managed-")).length ? <div className="object-skill-lifecycle"><h3>专属场景对象能力</h3>{data.skills.filter((skill) => skill.key.startsWith("managed-")).map((skill) => {
        const run = data.testRuns.find((item) => item.skillKey === skill.key && item.skillVersion === skill.version);
        return <article className="candidate-row" key={`${skill.key}-${skill.version}`}><div><strong>{skill.name}</strong><p>{skill.description}</p><small>{skill.version} · {{ draft: "待测试", validating: "校验中", testing: "测试中", trial_ready: "可试用", trial: "试用中", published: "已发布", archived: "已归档" }[skill.status]}{run ? ` · ${run.passed} 通过 / ${run.failed} 失败` : ""}</small></div>{canManageScope ? <div className="editor-actions">
          {skill.status === "draft" ? <button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => void runAction(`object-skill-test-${skill.key}`, async () => { const result = await api.testSkill(skill.key); return result.status === "passed" ? `${skill.name} 测试通过，可以进入试用。` : `${skill.name} 有 ${result.failed} 项测试未通过。`; })}>运行测试</button> : null}
          {skill.status === "trial_ready" ? <button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => void runAction(`object-skill-trial-${skill.key}`, async () => { await api.startSkillTrial(skill.key); return `${skill.name} 已进入试用。`; })}>开始试用</button> : null}
          {skill.status === "trial" ? <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void runAction(`object-skill-publish-${skill.key}`, async () => { await api.publishSkill(skill.key); return `${skill.name} 已发布，可用于场景编译。`; })}>发布能力</button> : null}
          {skill.status === "published" ? <span className="connection-pill connection-pill--ok">可用于场景</span> : null}
        </div> : null}</article>;
      })}</div> : null}
    </section>
    <section className="admin-card"><h2>最近快照与差异</h2><div className="summary-list"><SummaryRow label="最新快照" value={latestSnapshot ? `${formatTime(latestSnapshot.createdAt)} · ${latestSnapshot.status}` : "无"} /><SummaryRow label="对象与字段" value={latestSnapshot ? `${latestSnapshot.objectCatalogs.length} 个对象 · ${latestSnapshot.objectCatalogs.reduce((total, object) => total + object.fields.length, 0)} 个字段` : "—"} /><SummaryRow label="Record Type" value={latestSnapshot ? `${latestSnapshot.objectCatalogs.reduce((total, object) => total + (object.recordTypes?.length ?? 0), 0)} 个` : "—"} /><SummaryRow label="一级关系" value={latestSnapshot ? `${latestSnapshot.objectCatalogs.reduce((total, object) => total + (object.relationships?.length ?? 0), 0)} 条` : "—"} /><SummaryRow label="权限参照矩阵" value={latestSnapshot ? `${latestSnapshot.permissionMatrix?.length ?? 0} 条设计期参照；运行时仍验证当前用户` : "—"} /><SummaryRow label="写入规则" value={latestSnapshot ? `${latestSnapshot.validationRules?.filter((rule) => rule.active).length ?? 0} 条验证规则 · ${latestSnapshot.duplicateRules?.filter((rule) => rule.active).length ?? 0} 条重复规则` : "—"} /><SummaryRow label="扫描状态" value={latestSnapshot ? blockingWarnings.length ? `${blockingWarnings.length} 项阻断警告，发布前必须处理` : informationalNotices.length ? `${informationalNotices.length} 项兼容性提示，无阻断警告` : "无警告" : "—"} /><SummaryRow label="Schema Fingerprint" value={data.schemaFingerprints[0]?.value.slice(0, 16) ?? latestSnapshot?.schemaHash.slice(0, 16) ?? "—"} /><SummaryRow label="最新分析任务" value={data.analysisRuns[0] ? `${data.analysisRuns[0].status} · ${data.analysisRuns[0].selectedObjectApiNames.length} 个对象` : "—"} /><SummaryRow label="最新差异" value={data.diffs[0] ? `${data.diffs[0].severity} · ${data.diffs[0].changes.length} 项` : "首次快照尚无差异"} /></div>{latestSnapshot?.scanWarnings?.length ? <div className="admin-warning-list">{latestSnapshot.scanWarnings.map((warning) => <p key={warning}>{warning}</p>)}</div> : null}</section>
  </div>;
}

function UsersPage({ users, scenarios, currentUserId, busy, runAction }: {
  users: PlatformUser[];
  scenarios: BusinessScenario[];
  currentUserId: string;
  busy: string;
  runAction: (name: string, action: () => Promise<string>) => Promise<void>;
}) {
  const [invitation, setInvitation] = useState<{
    displayName: string;
    email: string;
    accountType: PlatformAccountType;
  }>({
    displayName: "",
    email: "",
    accountType: "user"
  });
  const [accessDrafts, setAccessDrafts] = useState<Record<string, {
    loaded: boolean;
    salesforceUserId: string;
    entitlementActive: boolean;
    scenarioIds: string[];
    roleKeys: string;
    expiresAt: string;
  }>>({});
  const publishedScenarios = scenarios.filter(({ status }) => status === "published");

  const loadAccess = async (user: PlatformUser) => {
    const access = await api.getUserAccess(user.id);
    setAccessDrafts((current) => ({
      ...current,
      [user.id]: {
        loaded: true,
        salesforceUserId: access.binding?.salesforceUserId ?? user.salesforceUserId ?? "",
        entitlementActive: access.entitlement?.status === "active",
        scenarioIds: access.entitlement?.scenarioIds ?? [],
        roleKeys: access.entitlement?.roleKeys.join(", ") ?? "",
        expiresAt: access.entitlement?.expiresAt?.slice(0, 16) ?? ""
      }
    }));
  };

  return <div className="admin-page-stack">
    <section className="admin-card">
      <h2>添加 AI 用户</h2>
      <p className="muted">先建立管理员或普通用户账号，再绑定该用户自己的 Salesforce User ID、AI 使用资格和已发布场景。创建账号不会自动授予业务数据权限。</p>
      <div className="inline-form">
        <label>姓名<input value={invitation.displayName} onChange={(event) => setInvitation((current) => ({ ...current, displayName: event.target.value }))} /></label>
        <label>用户邮箱<input type="email" value={invitation.email} onChange={(event) => setInvitation((current) => ({ ...current, email: event.target.value }))} /></label>
        <label>用户类型<select value={invitation.accountType} onChange={(event) => setInvitation((current) => ({
          ...current,
          accountType: event.target.value as PlatformAccountType
        }))}><option value="user">普通用户</option><option value="admin">管理员</option></select></label>
        <button className="button button--primary" type="button" disabled={!invitation.displayName.trim() || !invitation.email.includes("@") || Boolean(busy)} onClick={() => void runAction("invite-user", async () => {
          const user = await api.createUser(invitation);
          setInvitation({ displayName: "", email: "", accountType: "user" });
          return user.status === "invited"
            ? `已创建 ${user.displayName} 并发送登录邀请。`
            : `已创建 ${user.displayName}；请继续配置 Salesforce 绑定和 AI 使用资格。`;
        })}>添加用户</button>
      </div>
    </section>
    <section className="admin-card">
      <h2>用户与权限</h2>
      <p className="muted">运行请求必须同时通过平台用户、Org/User 绑定、entitlement、场景分配和 Salesforce 当前用户权限。保存资格后，普通用户首次打开嵌入助手或独立工作台即可自助连接本人 Salesforce，无需管理员生成或转发一次性授权链接。</p>
      <div className="admin-table">
        <div className="admin-table__head"><span>用户</span><span>用户类型</span><span>状态</span><span>操作</span></div>
        {users.map((user) => <div className="admin-table__row" key={user.id}>
          <span><strong>{user.displayName}</strong><small>{user.salesforceUserId ?? user.email}</small></span>
          <span><select aria-label={`${user.displayName} 的用户类型`} value={accountTypeOf(user)} disabled={user.id === currentUserId || Boolean(busy)} onChange={(event) => {
            const accountType = event.target.value as PlatformAccountType;
            void runAction(`account-type-${user.id}`, async () => {
              await api.updateUser(user.id, { status: user.status, accountType });
              return `${user.displayName} 已设为${accountTypeLabels[accountType]}。`;
            });
          }}><option value="user">普通用户</option><option value="admin">管理员</option></select></span>
          <span>{user.status === "active" ? "启用" : user.status === "invited" ? "待激活" : "已停用"}</span>
          <span><button className="text-button" type="button" disabled={user.id === currentUserId || Boolean(busy)} onClick={() => void runAction(`user-${user.id}`, async () => {
            await api.updateUser(user.id, {
              status: user.status === "active" ? "disabled" : "active",
              accountType: accountTypeOf(user)
            });
            return `${user.displayName} 已${user.status === "active" ? "停用" : "启用"}。`;
          })}>{user.status === "active" ? "停用" : "启用"}</button></span>
          <details className="user-access-editor">
            <summary onClick={() => {
              if (!accessDrafts[user.id]?.loaded) void loadAccess(user);
            }}>AI 访问资格与场景</summary>
            {accessDrafts[user.id]?.loaded ? (() => {
              const draft = accessDrafts[user.id]!;
              const updateDraft = (patch: Partial<typeof draft>) =>
                setAccessDrafts((current) => ({ ...current, [user.id]: { ...draft, ...patch } }));
              return <div className="inline-form">
                <label>Salesforce User ID<input value={draft.salesforceUserId} onChange={(event) => updateDraft({ salesforceUserId: event.target.value.trim() })} placeholder="005…" /></label>
                <label><input type="checkbox" checked={draft.entitlementActive} onChange={(event) => updateDraft({ entitlementActive: event.target.checked })} />启用 AI entitlement</label>
                <label>授权到期时间（可选）<input type="datetime-local" value={draft.expiresAt} onChange={(event) => updateDraft({ expiresAt: event.target.value })} /></label>
                <label>场景分组标签（可选，逗号分隔）<input value={draft.roleKeys} onChange={(event) => updateDraft({ roleKeys: event.target.value })} placeholder="例如：sales-team" /></label>
                <fieldset><legend>直接分配场景</legend>{publishedScenarios.length
                  ? publishedScenarios.map((item) => <label key={item.id}><input type="checkbox" checked={draft.scenarioIds.includes(item.id)} onChange={(event) => updateDraft({
                      scenarioIds: event.target.checked
                        ? [...new Set([...draft.scenarioIds, item.id])]
                        : draft.scenarioIds.filter((id) => id !== item.id)
                    })} />{item.name} · {item.linkedSkillVersion}</label>)
                  : <small>尚无已发布场景</small>}</fieldset>
                <button className="button button--primary" type="button" disabled={Boolean(busy) || !/^[A-Za-z0-9]{15,18}$/.test(draft.salesforceUserId)} onClick={() => void runAction(`access-${user.id}`, async () => {
                  await api.updateUserSalesforceBinding(user.id, {
                    salesforceUserId: draft.salesforceUserId,
                    status: "active"
                  });
                  await api.updateUserEntitlement(user.id, {
                    status: draft.entitlementActive ? "active" : "suspended",
                    scenarioIds: draft.scenarioIds,
                    roleKeys: [...new Set(draft.roleKeys.split(",").map((value) => value.trim()).filter((value) => /^[a-z][a-z0-9_-]{1,79}$/.test(value)))],
                    ...(draft.expiresAt ? { expiresAt: new Date(draft.expiresAt).toISOString() } : {})
                  });
                  return `${user.displayName} 的绑定、entitlement 和场景分配已保存。`;
                })}>保存 AI 访问配置</button>
              </div>;
            })() : <p className="muted">展开后读取当前授权配置。</p>}
          </details>
        </div>)}
      </div>
    </section>
  </div>;
}

function ModelsPage({ connection, providers, profiles, migration, busy, feedback, runAction }: {
  connection?: OrganizationConnection;
  providers: ModelProvider[];
  profiles: ModelProfile[];
  migration: SalesforceModelCredentialMigrationStatus;
  busy: string;
  feedback?: ActionFeedback;
  runAction: (name: string, action: () => Promise<string>) => Promise<void>;
}) {
  const presets: Record<ModelProvider["kind"], { label: string; name: string; protocol: ModelProvider["protocol"]; baseUrl: string; modelPlaceholder: string }> = {
    openai: { label: "OpenAI", name: "OpenAI", protocol: "responses", baseUrl: "https://api.openai.com/v1", modelPlaceholder: "例如 gpt-5.4" },
    dashscope: { label: "阿里云百炼 / 千问", name: "阿里云百炼", protocol: "chat_completions", baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1", modelPlaceholder: "填写百炼控制台中已开通的千问模型 ID" },
    deepseek: { label: "DeepSeek（官方直连）", name: "DeepSeek", protocol: "chat_completions", baseUrl: "https://api.deepseek.com", modelPlaceholder: "例如 deepseek-v4-flash 或 deepseek-v4-pro" },
    moonshot: { label: "Kimi（中国站）", name: "Kimi", protocol: "chat_completions", baseUrl: "https://api.moonshot.cn/v1", modelPlaceholder: "例如 kimi-k3；请以中国站控制台可用模型为准" },
    zhipu: { label: "智谱 GLM", name: "智谱 GLM", protocol: "chat_completions", baseUrl: "https://open.bigmodel.cn/api/paas/v4", modelPlaceholder: "例如 glm-5.2" },
    openai_compatible: { label: "自定义 OpenAI 兼容服务", name: "自定义模型服务", protocol: "chat_completions", baseUrl: "", modelPlaceholder: "填写服务商提供的模型 ID" },
    salesforce_openai_gateway: { label: "OpenAI（密钥存 Salesforce，推荐）", name: "OpenAI（Salesforce 托管凭据）", protocol: "responses", baseUrl: "https://api.openai.com/v1", modelPlaceholder: "例如 gpt-5.4" }
  };
  const isAlibabaCloud = connection?.cloudVariant === "alibaba_cloud";
  const initialKind: ModelProvider["kind"] = isAlibabaCloud ? "dashscope" : "openai";
  const [form, setForm] = useState<{
    name: string;
    kind: ModelProvider["kind"];
    protocol: ModelProvider["protocol"];
    baseUrl: string;
    authMode: "bearer" | "api-key";
    apiKey: string;
    connectionProviderId: string;
    model: string;
    profileName: string;
    role: NonNullable<ModelProfile["role"]>;
    maxContextTokens: number;
    maxOutputTokens: number;
  }>({
    name: presets[initialKind].name,
    kind: initialKind,
    protocol: presets[initialKind].protocol,
    baseUrl: presets[initialKind].baseUrl,
    authMode: "bearer" as const,
    apiKey: "",
    connectionProviderId: "",
    model: "",
    profileName: "AI 助手默认模型",
    role: "main_reasoning",
    maxContextTokens: 32_000,
    maxOutputTokens: 4_096
  });
  const [modelCatalog, setModelCatalog] = useState<ModelCatalog>();
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [manualModelEntry, setManualModelEntry] = useState(false);
  const [speechProbe, setSpeechProbe] = useState<{
    fileName: string;
    mimeType: "audio/webm" | "audio/ogg" | "audio/mp4" | "audio/mpeg" | "audio/wav";
    dataBase64: string;
  }>();
  const [speechProbeState, setSpeechProbeState] = useState<"idle" | "recording" | "ready">("idle");
  const speechRecorderRef = useRef<MediaRecorder | null>(null);
  const speechStreamRef = useRef<MediaStream | null>(null);
  const speechChunksRef = useRef<Blob[]>([]);
  const invalidateCatalog = () => {
    setModelCatalog(undefined);
    setCatalogError("");
  };
  const choosePreset = (kind: ModelProvider["kind"]) => {
    const preset = presets[kind];
    invalidateCatalog();
    setManualModelEntry(false);
    setSpeechProbe(undefined);
    setSpeechProbeState("idle");
    setForm((current) => ({
      ...current,
      kind,
      connectionProviderId: "",
      name: preset.name,
      protocol: preset.protocol,
      baseUrl: preset.baseUrl,
      model: ""
    }));
  };
  const customProvider = form.kind === "openai_compatible" && !form.connectionProviderId;
  const availablePresets = Object.entries(presets).filter(([kind]) => kind !== "salesforce_openai_gateway");
  const reusableConnections = providers.filter((provider, index, items) =>
    provider.credentialLocation === "platform_vault" && provider.status === "verified" && !provider.credentialSourceProviderId &&
    items.findIndex((candidate) => candidate.id === provider.id) === index
  );
  const canSubmit = Boolean(form.model && form.baseUrl && !busy && !catalogBusy && (form.connectionProviderId || form.apiKey.length >= 20) &&
    (form.role !== "speech_to_text" || speechProbe));
  const roleLabels = { main_reasoning: "对话与任务推理", image_document: "视觉理解", ocr_parser: "文档解析与 OCR", speech_to_text: "语音识别", fallback: "故障切换（旧配置）" } as const;
  const blockedDetail = [...new Set([...migration.activeLegacyRoles, ...migration.missingRuntimeRoles])].map((role) => roleLabels[role]).join("、");
  const activeProfiles = [...profiles]
    .filter((profile) => profile.enabled)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
    .filter((profile, index, items) => {
      const role = profile.role ?? (profile.isDefault ? "main_reasoning" : "fallback");
      return items.findIndex((candidate) => (candidate.role ?? (candidate.isDefault ? "main_reasoning" : "fallback")) === role) === index;
    });
  const configuredModels = activeProfiles.flatMap((profile) => {
    const provider = providers.find((candidate) => candidate.id === profile.providerId);
    return provider ? [{ profile, provider }] : [];
  });
  const selectedCurrent = configuredModels.find(({ profile }) =>
    (profile.role ?? (profile.isDefault ? "main_reasoning" : "fallback")) === form.role
  );
  const beginReplacement = (profile: ModelProfile, provider: ModelProvider) => {
    invalidateCatalog();
    setManualModelEntry(false);
    setSpeechProbe(undefined);
    setSpeechProbeState("idle");
    setForm({
      name: provider.name,
      kind: provider.kind,
      protocol: provider.protocol,
      baseUrl: provider.baseUrl,
      authMode: provider.authMode,
      apiKey: "",
      connectionProviderId: provider.credentialSourceProviderId ?? provider.id,
      model: profile.model,
      profileName: profile.name,
      role: profile.role ?? (profile.isDefault ? "main_reasoning" : "fallback"),
      maxContextTokens: profile.maxContextTokens,
      maxOutputTokens: profile.maxOutputTokens
    });
    window.requestAnimationFrame(() => {
      const formElement = document.getElementById("model-provider-form");
      if (typeof formElement?.scrollIntoView === "function") formElement.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const toggleSpeechProbeRecording = async () => {
    if (speechProbeState === "recording") {
      speechRecorderRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setCatalogError("当前浏览器不支持录音验证，请使用最新版 Chrome 或 Edge");
      return;
    }
    setCatalogError("");
    setSpeechProbe(undefined);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"]
        .find((candidate) => MediaRecorder.isTypeSupported(candidate));
      const recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 24_000 });
      speechStreamRef.current = stream;
      speechRecorderRef.current = recorder;
      speechChunksRef.current = [];
      recorder.ondataavailable = (event) => { if (event.data.size) speechChunksRef.current.push(event.data); };
      recorder.onerror = () => {
        stream.getTracks().forEach((track) => track.stop());
        setSpeechProbeState("idle");
        setCatalogError("测试录音中断，请重新录制");
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const audioType = (recorder.mimeType.split(";")[0] || "audio/webm") as "audio/webm" | "audio/ogg" | "audio/mp4";
        const extension = audioType === "audio/ogg" ? "ogg" : audioType === "audio/mp4" ? "m4a" : "webm";
        const audio = new Blob(speechChunksRef.current, { type: audioType });
        const reader = new FileReader();
        reader.onload = () => {
          const dataBase64 = String(reader.result).split(",", 2)[1] ?? "";
          if (!dataBase64) {
            setSpeechProbeState("idle");
            setCatalogError("没有录到有效声音，请重新录制");
            return;
          }
          setSpeechProbe({ fileName: `speech-capability-probe.${extension}`, mimeType: audioType, dataBase64 });
          setSpeechProbeState("ready");
        };
        reader.onerror = () => {
          setSpeechProbeState("idle");
          setCatalogError("无法读取测试录音，请重新录制");
        };
        reader.readAsDataURL(audio);
        speechRecorderRef.current = null;
        speechStreamRef.current = null;
        speechChunksRef.current = [];
      };
      recorder.start(500);
      setSpeechProbeState("recording");
      window.setTimeout(() => { if (recorder.state === "recording") recorder.stop(); }, 6_000);
    } catch (cause) {
      setSpeechProbeState("idle");
      setCatalogError(microphoneStartErrorMessage(cause));
    }
  };

  useEffect(() => () => {
    if (speechRecorderRef.current?.state === "recording") speechRecorderRef.current.stop();
    speechStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);
  const discoverModels = async () => {
    setCatalogBusy(true);
    setCatalogError("");
    try {
      const catalog = await api.discoverModelCatalog({
        kind: form.kind,
        protocol: form.protocol,
        baseUrl: form.baseUrl,
        authMode: form.authMode,
        ...(form.connectionProviderId ? { connectionProviderId: form.connectionProviderId } : { apiKey: form.apiKey }),
        role: form.role
      });
      setModelCatalog(catalog);
      setManualModelEntry(false);
      setForm((current) => ({
        ...current,
        model: catalog.models.some((item) => item.id === current.model) ? current.model : ""
      }));
    } catch (cause) {
      setModelCatalog(undefined);
      setManualModelEntry(true);
      setCatalogError(cause instanceof Error ? cause.message : "模型目录读取失败，请手动输入模型 ID");
    } finally {
      setCatalogBusy(false);
    }
  };
  const migrationPanel = migration.state === "not_required" ? null : <section className="admin-card"><h2>V1.6 模型凭据迁移</h2>{migration.state === "blocked" ? <p className="admin-error-banner">仍需迁移并验证 Runtime 角色：{blockedDetail || "无法识别的旧 Provider"}。只有相同角色的 Runtime Vault 模型通过真实能力探测后，服务端才允许撤销旧密钥。</p> : migration.state === "revoked" ? <p className="success-banner">Salesforce 已确认旧模型凭据不存在，V1.6 Provider 已停用。</p> : migration.state === "verification_required" || !migration.authorizationReady ? <p className="admin-error-banner">请先完成 Salesforce 管理员 Org 授权，以只读确认旧凭据状态并执行最终撤销；Integration User 不能代替管理员完成此操作。</p> : <><p className="success-banner">所有旧角色都已有启用、密钥在 Runtime Vault 且通过当前能力探测的替代模型，可以撤销 Salesforce External Credential 中的旧密钥。</p><button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => { if (!window.confirm("确认撤销 V1.6 Salesforce External Credential 中的模型密钥？服务端会再次检查每个旧角色均已有真实验证通过的 Runtime 替代模型。")) return; void runAction("model-credential-revoke", async () => { await api.revokeSalesforceModelCredential(); return "V1.6 Salesforce 模型密钥已撤销，旧 Provider 已停用。"; }); }}>撤销 V1.6 Salesforce 模型密钥</button></>}</section>;
  return <div className="admin-page-stack">{migrationPanel}<section className="admin-card" id="model-provider-form"><h2>{selectedCurrent ? `调整${roleLabels[form.role]}` : "配置 AI 服务"}</h2><p className="muted">服务商连接集中保存 API Key；不同服务用途只选择连接和模型。同一连接可供多个用途复用，密钥不会写入 Salesforce 记录、安装包或浏览器缓存。</p>{selectedCurrent ? <p className="model-replacement-notice">当前为 {selectedCurrent.provider.name} · {selectedCurrent.profile.model}。新配置会先完成真实验证，成功后再原位替换；验证失败不会影响当前服务。</p> : null}<div className="model-form">
    <label>服务商连接<select value={form.connectionProviderId} onChange={(event) => { const connectionId = event.target.value; invalidateCatalog(); setManualModelEntry(false); setSpeechProbe(undefined); setSpeechProbeState("idle"); if (!connectionId) { setForm((current) => ({ ...current, connectionProviderId: "", apiKey: "", model: "" })); return; } const provider = providers.find((candidate) => candidate.id === connectionId); if (!provider) return; setForm((current) => ({ ...current, connectionProviderId: provider.id, name: provider.name, kind: provider.kind, protocol: provider.protocol, baseUrl: provider.baseUrl, authMode: provider.authMode, apiKey: "", model: "" })); }}><option value="">新建服务商连接</option>{reusableConnections.map((provider) => <option key={provider.id} value={provider.id}>{provider.name} · {presets[provider.kind].label}</option>)}</select></label>
    <label>服务商<select disabled={Boolean(form.connectionProviderId)} value={form.kind} onChange={(event) => choosePreset(event.target.value as ModelProvider["kind"])}>{availablePresets.map(([kind, preset]) => <option key={kind} value={kind}>{preset.label}</option>)}</select></label>
    <label>连接名称<input disabled={Boolean(form.connectionProviderId)} value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></label>
    <label>API 协议<select disabled={!customProvider} value={form.protocol} onChange={(event) => { invalidateCatalog(); setForm((current) => ({ ...current, protocol: event.target.value as ModelProvider["protocol"], model: "" })); }}><option value="responses">Responses API</option><option value="chat_completions">Chat Completions</option></select></label>
    <label>Base URL<input disabled={!customProvider} value={form.baseUrl} onChange={(event) => { invalidateCatalog(); setForm((current) => ({ ...current, baseUrl: event.target.value, model: "" })); }} /></label>{customProvider ? <small>仅可使用服务器白名单内的 HTTPS 域名。</small> : <small>内置服务商固定使用官方 API 域名。</small>}
    {form.connectionProviderId ? <div className="model-connection-summary"><strong>已复用 Runtime Vault 中的连接</strong><small>无需重复填写 API Key；本次只验证并分配所选用途的模型。</small></div> : <label>{selectedCurrent ? "新 API 密钥（验证成功后替换）" : "API 密钥（保存到 Runtime Vault）"}<input type="password" autoComplete="new-password" value={form.apiKey} onChange={(event) => { const clearSelectedModel = Boolean(modelCatalog); invalidateCatalog(); setForm((current) => ({ ...current, apiKey: event.target.value, model: clearSelectedModel ? "" : current.model })); }} /></label>}
    <label>服务用途<select value={form.role} onChange={(event) => { invalidateCatalog(); setManualModelEntry(false); setSpeechProbe(undefined); setSpeechProbeState("idle"); setForm((current) => ({ ...current, role: event.target.value as NonNullable<ModelProfile["role"]>, model: "" })); }}><option value="main_reasoning">对话与任务推理（必需）</option><option value="image_document">视觉理解（图片 / 图文）</option><option value="ocr_parser">文档解析与 OCR</option><option value="speech_to_text">语音识别</option></select></label>
    <div className="model-catalog model-form__wide">
      <div className="model-catalog__heading"><div><strong>选择模型</strong><small>通过当前服务商连接实时读取，不根据展示名称猜测模型 ID，也不会在浏览器中保存密钥。</small></div><button className="button button--secondary" type="button" aria-busy={catalogBusy} disabled={Boolean(busy) || catalogBusy || !form.baseUrl || (!form.connectionProviderId && form.apiKey.length < 20)} onClick={() => void discoverModels()}>{catalogBusy ? "正在读取…" : modelCatalog ? "刷新模型列表" : "读取可用模型"}</button></div>
      {catalogBusy ? <div className="admin-info-banner" role="status" aria-live="polite">正在向服务商读取当前密钥可用的模型，通常几秒内完成。</div> : null}
      {catalogError ? <div className="admin-error-banner" role="alert">{catalogError}</div> : null}
      {modelCatalog ? <><label>可用模型<select value={manualModelEntry ? "" : form.model} onChange={(event) => setForm((current) => ({ ...current, model: event.target.value }))}><option value="">请选择适合“{roleLabels[form.role]}”的模型</option>{modelCatalog.models.map((model) => <option key={model.id} value={model.id}>{model.label !== model.id ? `${model.label} · ${model.id}` : model.id}{model.capabilities?.length ? ` · ${model.capabilities.join("/")}` : ""}{selectedCurrent?.profile.model === model.id ? "（当前）" : ""}</option>)}</select></label><small className="model-catalog__result">已按“{presets[form.kind].label} + {roleLabels[form.role]}”筛选 {modelCatalog.models.length} 个可用模型 · 仅在本页当前配置期间保留</small></> : null}
      <button className="text-button model-catalog__manual-toggle" type="button" aria-expanded={manualModelEntry} onClick={() => setManualModelEntry((current) => !current)}>{manualModelEntry ? "收起手动输入" : modelCatalog ? "列表中没有需要的模型？手动输入模型 ID" : "供应商无法列出模型？手动输入模型 ID"}</button>
      {manualModelEntry ? <label>手动模型 ID<input value={form.model} onChange={(event) => setForm((current) => ({ ...current, model: event.target.value }))} placeholder={presets[form.kind].modelPlaceholder} /></label> : null}
    </div>
    <label>最大上下文<input type="number" min={1024} value={form.maxContextTokens} onChange={(event) => setForm((current) => ({ ...current, maxContextTokens: Number(event.target.value) }))} /></label>
    <label>最大输出<input type="number" min={128} value={form.maxOutputTokens} onChange={(event) => setForm((current) => ({ ...current, maxOutputTokens: Number(event.target.value) }))} /></label>
    {form.role === "speech_to_text" ? <div className="model-catalog model-form__wide"><div className="model-catalog__heading"><div><strong>真实语音能力验证</strong><small>点击后清晰说出“语音测试上海”，再次点击结束；录音只用于本次验证，不会保存到 Runtime 或 Salesforce。</small></div><button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => void toggleSpeechProbeRecording()}>{speechProbeState === "recording" ? "结束测试录音" : speechProbeState === "ready" ? "重新录制" : "录制测试语音"}</button></div>{speechProbeState === "recording" ? <div className="admin-info-banner" role="status">正在录音，请说“语音测试上海”……最多 6 秒后自动结束。</div> : speechProbeState === "ready" ? <div className="success-banner" role="status"><CheckIcon size={17} />测试录音已就绪；保存时会直接调用所选模型验证转写能力。</div> : <small>语音角色不会使用文本对话探测，必须以真实音频验证成功后才能启用。</small>}</div> : null}
  </div><p className="muted">系统会按服务用途真实验证文本、结构化输出、视觉、文档、OCR 和语音能力，并明确展示已通过、未通过和未测试。每个用途最多启用一个主服务；故障切换在对话与任务推理的容灾策略中管理。</p><div className="admin-actions"><button className="button button--primary" type="button" aria-busy={busy === "model-create"} disabled={!canSubmit} onClick={() => void runAction("model-create", async () => { const replacing = Boolean(selectedCurrent); const result = await api.createModelProvider({ ...form, apiKey: form.connectionProviderId ? undefined : form.apiKey, ...(form.role === "speech_to_text" && speechProbe ? { speechProbe } : {}) }); setForm((current) => ({ ...current, apiKey: "" })); setSpeechProbe(undefined); setSpeechProbeState("idle"); return replacing ? `${result.provider.name} 已验证并更新${roleLabels[form.role]}。` : `${result.provider.name} 已通过能力验证并分配给${roleLabels[form.role]}。`; })}>{busy === "model-create" ? "正在执行能力验证…" : selectedCurrent ? "验证并更新服务用途" : "验证并保存"}</button></div>{busy === "model-create" || feedback?.status === "running" ? <div className="admin-info-banner" role="status" aria-live="polite">正在验证服务连接和所选模型能力，通常需要 10–90 秒。验证完成前当前服务继续运行，请勿重复点击。</div> : feedback?.status === "error" ? <div className="admin-error-banner" role="alert">模型验证未通过：{feedback.message}。当前服务配置和密钥未修改。</div> : feedback?.status === "success" ? <div className="success-banner" role="status"><CheckIcon size={17} />{feedback.message}</div> : null}</section><section className="admin-card"><h2>当前服务分配</h2>{configuredModels.length ? <div className="candidate-list">{configuredModels.map(({ provider, profile }) => { const storedInSalesforce = provider.credentialLocation === "salesforce_external_credential"; const role = profile.role ?? (profile.isDefault ? "main_reasoning" : "fallback"); const supportedCapabilities = modelCapabilityNames(provider, "supported"); const unsupportedCapabilities = modelCapabilityNames(provider, "unsupported"); const untestedCapabilities = modelCapabilityNames(provider, "not_tested"); return <article className="candidate-row" key={profile.id}><div><strong>{roleLabels[role]}</strong><p>{provider.name} · {profile.model}</p><small>{provider.baseUrl} · {provider.protocol} · {provider.status} · {storedInSalesforce ? "V1.6 Salesforce 凭据（待迁移）" : provider.credentialSourceProviderId ? "复用服务商连接" : "Runtime Vault 连接"}</small>{provider.capabilityProbe ? <div className="model-capability-summary" aria-label="模型能力验证结果"><small className="model-capability-summary__supported">已通过：{supportedCapabilities.length ? supportedCapabilities.join("、") : "无"} · {provider.capabilityProbe.latencyMs}ms</small>{unsupportedCapabilities.length ? <small className="model-capability-summary__unsupported">未通过：{unsupportedCapabilities.join("、")}</small> : null}{untestedCapabilities.length ? <small className="model-capability-summary__untested">未测试：{untestedCapabilities.join("、")}</small> : null}</div> : <small>能力状态：等待重新验证</small>}</div><div className="candidate-row__actions"><button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => beginReplacement(profile, provider)}>调整模型</button>{role !== "speech_to_text" ? <button className="button button--secondary" type="button" disabled={Boolean(busy)} onClick={() => void runAction(`model-${provider.id}`, async () => { await api.verifyModelProvider(provider.id); return `${provider.name} 标准能力验证通过。`; })}>重新验证</button> : null}</div></article>; })}</div> : <p className="empty-copy">尚未配置平台 AI 服务。生产环境不会自动降级为模拟回复。</p>}</section></div>;
}

function BusinessScenariosPage({ scenarios, capabilities, skills, testRuns, busy, runAction, canManage }: {
  scenarios: BusinessScenario[];
  capabilities: ObjectCapabilitySummary[];
  skills: SkillManifest[];
  testRuns: TestRun[];
  busy: string;
  runAction: (name: string, action: () => Promise<string>) => Promise<void>;
  canManage: boolean;
}) {
  const [selectedId, setSelectedId] = useState("");
  const [creating, setCreating] = useState(scenarios.length === 0);
  const selected = scenarios.find((scenario) => scenario.id === selectedId) ?? scenarios[0];
  const [name, setName] = useState("");
  const [businessGoal, setBusinessGoal] = useState("");
  const [exampleText, setExampleText] = useState("");
  const [selectedOperations, setSelectedOperations] = useState<Record<string, BusinessScenarioCapability["operations"]>>({});
  const statusLabels: Record<SkillManifest["status"], string> = {
    draft: "草稿待测试", validating: "正在校验", testing: "正在测试", trial_ready: "可开始试用",
    trial: "试用中", published: "已发布", archived: "已归档"
  };
  const operationLabels: Record<BusinessScenarioCapability["operations"][number], string> = {
    query: "查询", aggregate: "统计", create: "创建", update: "更新", delete: "删除"
  };

  useEffect(() => {
    if (creating || !selected) return;
    setSelectedId(selected.id);
    setName(selected.name);
    setBusinessGoal(selected.businessGoal);
    setExampleText(selected.exampleRequests.join("\n"));
    setSelectedOperations(Object.fromEntries(selected.capabilities.map((capability) => [capability.objectApiName, capability.operations])));
  }, [creating, selected?.id, selected?.updatedAt]);

  const beginCreate = () => {
    setCreating(true);
    setSelectedId("");
    setName("");
    setBusinessGoal("");
    setExampleText("");
    setSelectedOperations({});
  };
  const chooseScenario = (scenario: BusinessScenario) => {
    setCreating(false);
    setSelectedId(scenario.id);
  };
  const toggleOperation = (objectApiName: string, operation: BusinessScenarioCapability["operations"][number], checked: boolean) => {
    setSelectedOperations((current) => {
      const operations = current[objectApiName] ?? [];
      const next = checked
        ? [...new Set([...operations, operation])]
        : operations.filter((item) => item !== operation);
      return { ...current, [objectApiName]: next };
    });
  };
  const formCapabilities = capabilities.flatMap((capability) => {
    const operations = (selectedOperations[capability.objectApiName] ?? [])
      .filter((operation) => capability.operations.includes(operation));
    return operations.length ? [{ objectApiName: capability.objectApiName, operations }] : [];
  });
  const exampleRequests = [...new Set(exampleText.split("\n").map((value) => value.trim()).filter(Boolean))];
  const activeScenario = creating ? undefined : selected;
  const activeSkill = activeScenario
    ? skills.find((skill) => skill.key === activeScenario.linkedSkillKey && skill.version === activeScenario.linkedSkillVersion)
      ?? skills.find((skill) => skill.key === activeScenario.linkedSkillKey)
    : undefined;
  const canSave = canManage && name.trim().length > 0 && businessGoal.trim().length > 0 && exampleRequests.length > 0 && formCapabilities.length > 0;
  const save = async () => {
    const input = { name: name.trim(), businessGoal: businessGoal.trim(), exampleRequests, capabilities: formCapabilities };
    const saved = activeScenario
      ? await api.updateBusinessScenario(activeScenario.id, input)
      : await api.createBusinessScenario(input);
    setCreating(false);
    setSelectedId(saved.id);
    return activeScenario
      ? `${saved.name} 已保存为可测试草稿；对象关系、字段依赖和测试集已自动更新。`
      : `${saved.name} 已创建；系统已自动生成执行规则和测试集。`;
  };

  return <div className="admin-workspace admin-workspace--product">
    <aside className="skill-list-panel">
      <button className="button button--primary button--full" type="button" disabled={!canManage || Boolean(busy)} onClick={beginCreate}>新建业务场景</button>
      <p className="list-count">共 {scenarios.length} 个业务场景</p>
      <div className="skill-list">{scenarios.map((scenario) => <button type="button" key={scenario.id} className={!creating && scenario.id === selected?.id ? "skill-row skill-row--active" : "skill-row"} onClick={() => chooseScenario(scenario)}><div><strong>{scenario.name}</strong><span>{scenario.businessGoal}</span></div><small>{scenario.linkedSkillVersion} · {statusLabels[scenario.status]}</small></button>)}</div>
      {!scenarios.length ? <p className="empty-copy">用日常业务语言创建第一个场景，不需要配置流程图。</p> : null}
    </aside>
    <section className="skill-editor">
      <header className="skill-editor__header">
        <div><h2>{creating ? "新建业务场景" : activeScenario?.name}</h2><p>{creating ? "选择已发布的对象能力，系统自动生成规则、关系和测试" : `${activeScenario?.linkedSkillVersion} · ${activeScenario ? statusLabels[activeScenario.status] : ""}`}</p></div>
        {canManage ? <div className="editor-actions">
          <button className="button button--secondary" type="button" disabled={!canSave || Boolean(busy)} onClick={() => void runAction("scenario-save", save)}>{busy === "scenario-save" ? "保存中…" : activeScenario?.status === "published" ? "保存为新草稿版本" : "保存场景"}</button>
          {activeSkill?.status === "draft" ? <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void runAction("scenario-test", async () => { const run = await api.testSkill(activeSkill.key); return run.status === "passed" ? `${run.passed} 项自动测试全部通过，可以开始试用。` : `${run.failed} 项测试未通过，请根据结果调整场景。`; })}>运行自动测试</button> : null}
          {activeSkill?.status === "trial_ready" ? <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void runAction("scenario-trial", async () => { await api.startSkillTrial(activeSkill.key); return `${activeSkill.name} 已进入试用。`; })}>开始试用</button> : null}
          {activeSkill?.status === "trial" ? <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void runAction("scenario-publish", async () => { await api.publishSkill(activeSkill.key); return `${activeSkill.name} 已正式发布。`; })}>正式发布</button> : null}
          {activeSkill?.status === "published" ? <span className="connection-pill connection-pill--ok">已正式生效</span> : null}
        </div> : <span className="muted">只读查看</span>}
      </header>
      <div className="editor-grid">
        <div className="editor-form">
          <section><h3>1. 业务目标</h3><p className="muted">说明用户希望完成什么，不需要描述系统步骤。</p>
            <label>场景名称<input value={name} readOnly={!canManage} onChange={(event) => setName(event.target.value)} placeholder="例如：商机跟进" /></label>
            <label>业务目标<textarea rows={4} value={businessGoal} readOnly={!canManage} onChange={(event) => setBusinessGoal(event.target.value)} placeholder="例如：销售人员可以更新商机进展、安排拜访并创建后续任务。" /></label>
          </section>
          <section><h3>2. 可使用的对象能力</h3><p className="muted">只显示系统扫描并验证过的能力。未发布的对象能力不能加入正式场景。</p>
            <div className="scenario-capability-list">{capabilities.map((capability) => <article className={capability.ready ? "scenario-capability-card" : "scenario-capability-card scenario-capability-card--disabled"} key={capability.objectApiName}>
              <div className="scenario-capability-header"><div><strong>{capability.label}</strong><span>{capability.objectApiName}</span></div>{capability.ready ? <span className="connection-pill connection-pill--ok">已验证</span> : null}</div>
              {capability.ready ? <div className="scenario-operation-list">{capability.operations.map((operation) => <label key={operation}><input type="checkbox" disabled={!canManage} checked={(selectedOperations[capability.objectApiName] ?? []).includes(operation)} onChange={(event) => toggleOperation(capability.objectApiName, operation, event.target.checked)} />{operationLabels[operation]}</label>)}</div> : <small>{capability.blockers.join("；")}</small>}
            </article>)}</div>
          </section>
          <section><h3>3. 用户日常说法</h3><p className="muted">每行一条，帮助系统识别场景；跨对象需求直接写在同一句话中，系统会自动生成连续操作测试。</p><label>示例提问<textarea rows={6} value={exampleText} readOnly={!canManage} onChange={(event) => setExampleText(event.target.value)} placeholder={"帮我看看这个客户最近有哪些商机和待办。\n把常州项目关闭日期延后一个月，再安排一个跟进任务。"} /></label></section>
        </div>
        <aside className="validation-panel">
          <section><h3>系统自动处理</h3><div className="summary-list"><SummaryRow label="能力边界" value="按已发布对象技能校验" /><SummaryRow label="执行顺序" value="按用户指令顺序拆分" /><SummaryRow label="写入操作" value="统一展示，逐项或全部确认" /><SummaryRow label="失败处理" value="保留成功项，只重试失败项" /></div></section>
          <section><h3>场景就绪状态</h3>{activeScenario ? activeScenario.readiness.ready ? <p className="connection-pill connection-pill--ok">对象能力和 Schema 依赖已就绪</p> : <div className="admin-warning-list">{activeScenario.readiness.blockers.map((blocker) => <p key={blocker}>{blocker}</p>)}</div> : <p className="muted">保存后系统会检查对象能力、字段权限和当前 Org 快照。</p>}</section>
          <section><h3>最近自动测试</h3>{activeSkill ? <ReleaseEvidenceSummary skill={activeSkill} runs={testRuns} /> : <p className="muted">场景当前版本尚无可读取的技能证据。</p>}</section>
          {activeScenario ? <details className="scenario-details"><summary>技术信息</summary><div className="summary-list"><SummaryRow label="运行技能" value={activeScenario.linkedSkillKey} /><SummaryRow label="绑定快照" value={activeScenario.sourceSnapshotId.slice(0, 8)} /><SummaryRow label="对象数量" value={String(activeScenario.capabilities.length)} /><SummaryRow label="操作数量" value={String(activeScenario.capabilities.reduce((total, capability) => total + capability.operations.length, 0))} /></div></details> : null}
        </aside>
      </div>
    </section>
  </div>;
}

function SkillsPage({ skills, testRuns, busy, runAction, canManage }: { skills: SkillManifest[]; testRuns: TestRun[]; busy: string; runAction: (name: string, action: () => Promise<string>) => Promise<void>; canManage: boolean }) {
  const [selectedKey, setSelectedKey] = useState("");
  const [draft, setDraft] = useState<SkillManifest>();
  const [versions, setVersions] = useState<SkillManifest[]>([]);
  const [query, setQuery] = useState("");
  const visibleSkills = useMemo(() => skills.filter((skill) => `${skill.name}${skill.description}`.includes(query)), [query, skills]);
  const activeKey = selectedKey || visibleSkills[0]?.key || "";
  const stored = skills.find((skill) => skill.key === activeKey);
  const selected = draft?.key === activeKey ? draft : stored;
  const editable = Boolean(canManage && selected?.status === "draft");
  const qualityDefinitionCoverage = selected ? assessSkillQualityGateDefinitions(selected) : undefined;

  useEffect(() => {
    let cancelled = false;
    if (!activeKey) { setVersions([]); return; }
    void api.listSkillVersions(activeKey).then((items) => { if (!cancelled) setVersions(items); }).catch(() => { if (!cancelled) setVersions([]); });
    return () => { cancelled = true; };
  }, [activeKey, skills]);

  const selectSkill = (skill: SkillManifest) => { setSelectedKey(skill.key); setDraft(undefined); };
  const createEditableVersion = () => {
    if (!canManage || !selected) return;
    setDraft({ ...selected, version: bumpPatchVersion(selected.version), status: "draft" });
  };
  const updateDescription = (value: string) => {
    if (!selected) return;
    setDraft({
      ...selected,
      description: value,
      ...(selected.modules ? { modules: { ...selected.modules, topic: { ...selected.modules.topic, businessGoal: value } } } : {})
    });
  };
  const updateInstructions = (value: string) => {
    if (!selected) return;
    const lines = value.split("\n").map((line) => line.trim()).filter(Boolean);
    setDraft({
      ...selected,
      instructions: value,
      ...(selected.modules ? { modules: { ...selected.modules, instructions: lines.map((text, index) => ({ key: `instruction-${index + 1}`, text, priority: index + 1 })) } } : {})
    });
  };
  const updateActionFields = (actionKey: string, fieldType: "readableFields" | "writableFields", values: string[]) => {
    if (!selected?.modules) return;
    setDraft({
      ...selected,
      modules: {
        ...selected.modules,
        actions: selected.modules.actions.map((action) => action.key === actionKey ? { ...action, [fieldType]: values } : action)
      }
    });
  };
  const updateTestTurn = (caseKey: string, content: string) => {
    if (!selected?.modules) return;
    const originalDatasetVersion = stored?.modules?.testSuite.datasetVersion;
    const currentVersion = selected.modules.testSuite.datasetVersion;
    const datasetVersion = currentVersion === originalDatasetVersion ? bumpPatchVersion(currentVersion) : currentVersion;
    setDraft({
      ...selected,
      modules: {
        ...selected.modules,
        testSuite: {
          ...selected.modules.testSuite,
          datasetVersion,
          cases: selected.modules.testSuite.cases.map((testCase) => testCase.key === caseKey
            ? { ...testCase, turns: testCase.turns.map((turn, index) => turn.role === "user" && index === testCase.turns.findIndex((item) => item.role === "user") ? { ...turn, content } : turn) }
            : testCase)
        }
      }
    });
  };

  return <div className="admin-workspace admin-workspace--product">
    <aside className="skill-list-panel">
      <label className="search-field"><SearchIcon size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索技能" /></label>
      <p className="list-count">共 {visibleSkills.length} 个技能</p>
      <div className="skill-list">{visibleSkills.map((skill) => <button type="button" key={skill.key} className={skill.key === activeKey ? "skill-row skill-row--active" : "skill-row"} onClick={() => selectSkill(skill)}><div><strong>{skill.name}</strong><span>{skill.description}</span></div><small>{skill.version} · {skill.status}</small></button>)}</div>
    </aside>
    {selected ? <section className="skill-editor">
      <header className="skill-editor__header">
        <div><h2>{selected.name}</h2><p>版本 {selected.version} · {selected.status}</p></div>
        {canManage ? <div className="editor-actions">
          {selected.status === "published" ? <button className="button button--secondary" type="button" onClick={createEditableVersion}>创建新草稿版本</button> : null}
          {selected.status === "draft" ? <button className="button button--secondary" type="button" disabled={Boolean(busy) || !draft} onClick={() => void runAction("skill-save", async () => { await api.saveSkill(selected); setDraft(undefined); return `${selected.name} ${selected.version} 草稿已保存。`; })}>保存草稿</button> : null}
          <span className="muted">测试、试用、发布和回滚请在“测试与发布”中执行。</span>
        </div> : <span className="muted">只读查看</span>}
      </header>
      <div className="editor-grid">
        <div className="editor-form">
          {!selected.modules ? <section className="admin-warning-list"><p>这是旧版技能。请创建新草稿并保存，系统会补齐产品化模块后才能测试和发布。</p></section> : null}
          <section><h3>Topic 与 Instructions</h3>
            <label>业务目标<textarea value={selected.description} readOnly={!editable} rows={3} onChange={(event) => updateDescription(event.target.value)} /></label>
            <label>对话与业务约束<textarea value={selected.instructions} readOnly={!editable} rows={10} onChange={(event) => updateInstructions(event.target.value)} /></label>
          </section>
          <section><h3>Actions（最多 15 个）</h3>
            {selected.modules ? <div className="action-definition-list">{selected.modules.actions.map((action) => {
              const dependency = selected.modules!.schemaDependency.objects.find((object) => object.objectApiName === action.objectApiName);
              const availableFields = dependency?.fields.map((field) => field.apiName) ?? [];
              return <article className="action-definition-card" key={action.key}>
                <div><strong>{action.name}</strong><span>{action.objectApiName} · {action.operation} · {action.executionMode === "deterministic_program" ? "确定性执行" : action.executionMode}</span></div>
                <p>{action.description}</p>
                <div className="action-field-grid">
                  <label>可读字段<select multiple disabled={!editable} value={action.readableFields} onChange={(event) => updateActionFields(action.key, "readableFields", Array.from(event.currentTarget.selectedOptions, (option) => option.value))}>{availableFields.map((field) => <option key={field} value={field}>{field}</option>)}</select></label>
                  {action.operation === "create" || action.operation === "update" ? <label>可写字段<select multiple disabled={!editable} value={action.writableFields} onChange={(event) => updateActionFields(action.key, "writableFields", Array.from(event.currentTarget.selectedOptions, (option) => option.value))}>{availableFields.map((field) => <option key={field} value={field}>{field}</option>)}</select></label> : <div><span className="muted">只读 Action，无可写字段。</span></div>}
                </div>
                <small>{action.confirmation === "required" ? "写入前必须预览确认" : "查询可直接执行"}</small>
              </article>;
            })}</div> : <div className="permission-table"><div className="permission-table__head"><span>对象</span><span>操作</span><span>可写字段</span></div>{selected.allowedObjects.map((object) => <div className="permission-table__row" key={object.objectApiName}><strong>{object.objectApiName}</strong><span>{object.operations.join("、")}</span><span>{object.writableFields.length ? `${object.writableFields.length} 个字段` : "只读"}</span></div>)}</div>}
          </section>
          <section><h3>版本化测试集</h3>
            {selected.modules ? <><p className="muted">数据集 {selected.modules.testSuite.datasetId?.slice(0, 8) ?? "保存后创建"} · 版本 {selected.modules.testSuite.datasetVersion} · 支持多轮动作序列</p>{qualityDefinitionCoverage?.complete ? <p className="connection-pill connection-pill--ok">每个已开放对象和 Action 所需的质量门禁均已定义；仍需运行并通过后才能作为发布证据。</p> : <div className="admin-warning-list"><p>测试定义尚不完整：{qualityDefinitionCoverage?.missingCases.map(qualityRequirementLabel).join("、") || "旧版技能未建立产品化门禁"}。已定义不代表已运行通过。</p></div>}{selected.modules.testSuite.cases.map((testCase) => { const firstUserTurn = testCase.turns.find((turn) => turn.role === "user"); return <div className="test-row test-row--editable" key={testCase.key}><div><strong>{testCase.name}</strong>{firstUserTurn ? <textarea rows={2} readOnly={!editable} value={firstUserTurn.content} onChange={(event) => updateTestTurn(testCase.key, event.target.value)} /> : <span>缺少用户输入</span>}</div><small>{qualityGateLabel(testCase)}{testCase.targetObjectApiName ? ` · ${testCase.targetObjectApiName}` : ""} · {expectedTestOutcomeLabel(testCase)}</small></div>; })}</> : selected.testCases.map((testCase) => <div className="test-row" key={testCase.name}><div><strong>{testCase.name}</strong><span>{testCase.input}</span></div><small>{testCase.expectedOperation} · {testCase.expectedObject}</small></div>)}
          </section>
        </div>
        <aside className="validation-panel">
          <section><h3>最近测试</h3><ReleaseEvidenceSummary skill={selected} runs={testRuns} /></section>
          <section><h3>Policy</h3>{selected.modules ? <div className="summary-list"><SummaryRow label="零结果" value="明确未找到" /><SummaryRow label="多结果" value="列出并等待选择" /><SummaryRow label="重复" value="保留并提示" /><SummaryRow label="批量" value={`最多 ${selected.modules.policy.maxBatchSize} 条`} /></div> : <p className="muted">旧版技能尚无结构化策略。</p>}</section>
          <section><h3>Schema 依赖</h3>{selected.modules ? <><p className="muted">快照 {selected.modules.schemaDependency.snapshotId.slice(0, 8)} · {selected.modules.schemaDependency.objects.length} 个对象</p>{selected.modules.schemaDependency.objects.map((object) => <p key={object.objectApiName}><strong>{object.objectApiName}</strong><br /><small>{object.fields.length} 个字段 · {object.validationRules.length} 条验证规则 · {object.duplicateRules.length} 条重复规则</small></p>)}</> : <p className="muted">保存新草稿时自动绑定当前 Org 快照。</p>}</section>
          <section><h3>版本记录</h3>{versions.length ? versions.map((version) => <div className="version-row" key={version.version}><div><strong>{version.version}</strong><small>{version.status} · {version.modules?.actions.length ?? 0} Actions</small></div></div>) : <p className="muted">暂无历史版本。</p>}</section>
          <section><h3>发布门禁</h3><p className="muted">必须具备模块化结构、真实 Org 依赖、冻结测试集、当前版本测试通过并完成试用。</p></section>
        </aside>
      </div>
    </section> : <div className="empty-state">暂无技能。请先扫描 Org 并审核候选。</div>}
  </div>;
}

function TestingReleasePage({ skills, datasets, testRuns, busy, runAction, canManage }: {
  skills: SkillManifest[];
  datasets: TestDataset[];
  testRuns: TestRun[];
  busy: string;
  runAction: (name: string, action: () => Promise<string>) => Promise<void>;
  canManage: boolean;
}) {
  const [selectedKey, setSelectedKey] = useState(() => skills[0]?.key ?? "");
  const [versions, setVersions] = useState<SkillManifest[]>([]);
  const selected = skills.find((skill) => skill.key === selectedKey) ?? skills[0];
  const selectedDataset = selected ? datasets.find((dataset) => dataset.id === selected.modules?.testSuite.datasetId) ?? datasets.find((dataset) => dataset.skillKey === selected.key) : undefined;
  const runs = selected ? testRuns.filter((run) => run.skillKey === selected.key) : [];
  const evidence = selected ? assessReleaseEvidence(selected, runs) : undefined;
  const evidenceRun = evidence?.run;
  const passedVersions = new Set(runs.filter((run) => run.status === "passed").map((run) => run.skillVersion));

  useEffect(() => {
    let cancelled = false;
    if (!selected) { setVersions([]); return; }
    void api.listSkillVersions(selected.key).then((items) => { if (!cancelled) setVersions(items); }).catch(() => { if (!cancelled) setVersions([]); });
    return () => { cancelled = true; };
  }, [selected?.key, skills]);

  if (!selected) return <div className="empty-state">暂无技能。请先在 Org 分析中审核候选并生成草稿。</div>;
  const statusOrder: Array<SkillManifest["status"]> = ["draft", "validating", "testing", "trial_ready", "trial", "published"];
  const statusLabels: Record<SkillManifest["status"], string> = { draft: "草稿", validating: "校验中", testing: "测试中", trial_ready: "可试用", trial: "试用中", published: "已发布", archived: "已归档" };
  const currentIndex = statusOrder.indexOf(selected.status);
  return <div className="admin-page-stack">
    <section className="admin-card admin-card--header"><div><h2>受控测试与发布</h2><p>只有当前版本通过测试并完成试用，才能正式发布；已发布版本不可覆盖。</p></div><label>选择技能<select value={selected.key} onChange={(event) => setSelectedKey(event.target.value)}>{skills.map((skill) => <option key={skill.key} value={skill.key}>{skill.name} · {skill.version}</option>)}</select></label></section>
    <section className="admin-card"><h2>{selected.name}</h2><p className="muted">版本 {selected.version} · 当前状态：{statusLabels[selected.status]}</p><div className="chip-list">{statusOrder.map((status, index) => <span className={currentIndex >= index ? "scope-chip scope-chip--active" : "scope-chip"} key={status}>{statusLabels[status]}</span>)}</div>{canManage ? <div className="editor-actions">
      {selected.status === "draft" ? <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void runAction("release-test", async () => { const run = await api.testSkill(selected.key); return run.status === "passed" ? `${run.passed} 项测试全部通过，技能已可进入试用。` : `${run.failed} 项测试失败，请查看失败详情并返回技能工作台修正。`; })}>运行当前版本测试</button> : null}
      {selected.status === "trial_ready" ? <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void runAction("release-trial", async () => { await api.startSkillTrial(selected.key); return `${selected.name} ${selected.version} 已进入指定用户试用。`; })}>开始试用</button> : null}
      {selected.status === "trial" ? <button className="button button--primary" type="button" disabled={Boolean(busy)} onClick={() => void runAction("release-publish", async () => { await api.publishSkill(selected.key); return `${selected.name} ${selected.version} 已正式发布。`; })}>正式发布</button> : null}
      {selected.status === "published" ? <span className="connection-pill connection-pill--ok">当前版本已正式生效</span> : null}
    </div> : <p className="muted">当前角色只能查看测试和发布证据。</p>}</section>
    <div className="editor-grid">
      <section className="admin-card"><h2>冻结测试集</h2>{selectedDataset ? <div className="summary-list"><SummaryRow label="数据集" value={selectedDataset.name} /><SummaryRow label="状态" value={selectedDataset.status === "active" ? "启用" : "已归档"} /><SummaryRow label="绑定版本" value={selected.modules?.testSuite.datasetVersion ?? "旧版技能未绑定"} /><SummaryRow label="测试用例" value={`${selected.modules?.testSuite.cases.length ?? selected.testCases.length} 条`} /><SummaryRow label="最后更新" value={formatTime(selectedDataset.updatedAt)} /></div> : <p className="empty-copy">当前技能未绑定持久化测试集，不能作为发布证据。请先保存产品化草稿。</p>}
        <h3>当前版本最近测试</h3><ReleaseEvidenceSummary skill={selected} runs={runs} />{evidenceRun ? <div className="candidate-list">{evidenceRun.results.map((result) => <article className="candidate-row" key={result.name}><div><strong>{result.name}</strong><p>{testResultDescription(result)}</p><small>{testResultExpectation(result)} · {result.durationMs}ms</small></div><span className={result.status === "passed" ? "connection-pill connection-pill--ok" : "connection-pill connection-pill--warn"}>{result.status === "passed" ? "通过" : "失败"}</span></article>)}</div> : null}</section>
      <aside className="admin-card"><h2>版本与回滚</h2><p className="muted">只允许回滚到已有通过记录、且仍兼容当前 Org 快照的版本。</p>{versions.length ? versions.map((version) => <div className="version-row" key={version.version}><div><strong>{version.version}</strong><small>{statusLabels[version.status]} · {passedVersions.has(version.version) ? "已有通过证据" : "无通过证据"}</small></div>{canManage && version.version !== selected.version ? <button className="text-button" type="button" disabled={Boolean(busy) || !passedVersions.has(version.version)} onClick={() => void runAction(`release-rollback-${version.version}`, async () => { await api.rollbackSkill(selected.key, version.version); return `${selected.name} 已回滚并发布 ${version.version}。`; })}>回滚</button> : null}</div>) : <p className="empty-copy">暂无历史版本。</p>}</aside>
    </div>
  </div>;
}

function SystemSettingsPage({ status }: { status?: PlatformSystemStatus }) {
  if (!status) return <div className="empty-state">系统状态尚未加载。请刷新页面或检查平台管理员权限。</div>;
  const ready = (value: boolean) => value ? "已就绪" : "需要配置";
  const environmentLabel = status.environment === "production" ? "生产环境" : "开发环境";
  const regionLabel = status.regionProfile === "china" ? "中国大陆" : "Global";
  const salesforceCloudLabel = status.salesforce.cloudVariant === "alibaba_cloud"
    ? "Salesforce on Alibaba Cloud"
    : status.salesforce.cloudVariant === "global" ? "Salesforce Global" : "尚未验证";
  const modelGatewayLabel = status.modelGateway.mode === "development-mock" ? "内置开发适配器" : status.modelGateway.mode;
  return <div className="admin-page-stack">
    <section className="admin-card admin-card--header"><div><h2>运行环境</h2><p>这里显示服务端实际生效的配置，不展示密钥正文，也不提供不会影响运行端的空配置项。</p></div><span className={status.environment === "production" ? "connection-pill connection-pill--ok" : "connection-pill connection-pill--warn"}>{environmentLabel}</span></section>
    <section className="metric-grid">
      <MetricCard label="Runtime 区域" value={regionLabel} detail={status.salesforce.regionCompatible ? "与 Salesforce 云环境一致" : "等待匹配 Salesforce 云环境"} tone={status.salesforce.regionCompatible ? "normal" : "warning"} />
      <MetricCard label="关系数据库" value={status.persistence.kind} detail={ready(status.persistence.productionReady)} tone={status.persistence.productionReady ? "normal" : "warning"} />
      <MetricCard label="附件存储" value={status.attachmentStorage.kind.toUpperCase()} detail={ready(status.attachmentStorage.productionReady)} tone={status.attachmentStorage.productionReady ? "normal" : "warning"} />
      <MetricCard label="凭据加密" value={status.credentialVault.configured ? "已启用" : "未配置"} detail="模型与 OAuth 凭据保护" tone={status.credentialVault.configured ? "normal" : "warning"} />
      <MetricCard label="模型网关" value={modelGatewayLabel} detail={ready(status.modelGateway.configured)} tone={status.modelGateway.configured ? "normal" : "warning"} />
    </section>
    <div className="editor-grid">
      <section className="admin-card"><h2>身份与 Salesforce</h2><div className="summary-list">
        <StatusRow label="Salesforce 云区域" value={status.salesforce.regionCompatible} detail={`${salesforceCloudLabel} · ${regionLabel} Runtime`} />
        <StatusRow label="企业邮箱验证码" value={status.platformEmailAuth.enabled} detail={status.platformEmailAuth.deliveryMode === "smtp" ? "SMTP 真实邮件投递" : "仅控制台投递"} />
        <StatusRow label="工具栏票据校验" value={status.salesforce.ticketVerificationConfigured} detail="校验 Salesforce 签发的短时身份票据" />
        <StatusRow label="User Access OAuth" value={status.salesforce.userAccessOAuthConfigured} detail="普通用户以本人权限访问 Salesforce 业务数据" />
        <StatusRow label="Org 管理授权" value={status.salesforce.orgManagementConfigured} detail={`${status.salesforce.orgManagementMode} · 强制只读`} />
        <StatusRow label="可选 Integration User" value={status.salesforce.orgIntegrationConfigured} detail={status.salesforce.orgIntegrationConfigured ? `${status.salesforce.orgIntegrationMode} · 回退连接` : "未配置，不影响管理员授权模式"} />
      </div></section>
      <aside className="admin-card"><h2>固定安全策略</h2><div className="summary-list">
        <SummaryRow label="写入操作" value={status.guardrails.writePreviewRequired ? "必须预览并确认" : "未强制"} />
        <SummaryRow label="单文件上限" value={`${Math.round(status.guardrails.maxAttachmentBytes / 1024 / 1024)} MB`} />
        <SummaryRow label="单条附件总量" value={`${Math.round(status.guardrails.maxTotalAttachmentBytes / 1024 / 1024)} MB`} />
        <SummaryRow label="单次附件" value={`${status.guardrails.maxConversationAttachments} 个`} />
        <SummaryRow label="单次批量" value={`${status.guardrails.maxBatchSize} 条`} />
        <SummaryRow label="近期上下文" value={`${status.guardrails.recentContextTurns} 轮原始对话`} />
        <SummaryRow label="滚动摘要" value={`${status.guardrails.contextSummaryMessageThreshold} 条消息后生成`} />
        <SummaryRow label="Org 管理连接" value={status.guardrails.integrationReadOnly ? "永久只读" : "允许写入"} />
      </div><p className="muted">这些策略属于产品安全基线，不能通过页面绕过。</p></aside>
    </div>
    <section className="admin-card"><h2>状态证据</h2><div className="summary-list"><SummaryRow label="Organization ID" value={status.organizationId} /><SummaryRow label="运行版本" value={status.runtimeVersion} /><SummaryRow label="评估时间" value={formatTime(status.evaluatedAt)} /></div></section>
  </div>;
}

function StatusRow({ label, value, detail }: { label: string; value: boolean; detail: string }) {
  return <div className="summary-row"><span>{label}<small>{detail}</small></span><strong className={value ? "connection-pill connection-pill--ok" : "connection-pill connection-pill--warn"}>{value ? "已就绪" : "需要配置"}</strong></div>;
}

function AuditPage({ events, overview }: { events: AuditEvent[]; overview?: PlatformRuntimeOverview }) {
  const actionStateLabels: Record<PlatformRuntimeOverview["recentActions"][number]["state"], string> = {
    draft: "草稿", pending_confirmation: "待确认", needs_attention: "需要处理", executing: "执行中", succeeded: "成功", failed: "失败", cancelled: "已取消", expired: "已过期"
  };
  const operationLabels: Record<PlatformRuntimeOverview["recentActions"][number]["operation"], string> = {
    query: "查询", aggregate: "统计", create: "创建", update: "更新", delete: "删除"
  };
  return <div className="admin-page-stack">
    {overview ? <>
      <section className="metric-grid">
        <MetricCard label="会话总数" value={String(overview.metrics.conversations)} detail={`最近 24 小时 ${overview.metrics.conversations24h} 个活跃会话`} />
        <MetricCard label="业务动作" value={String(overview.metrics.actions)} detail={`${overview.metrics.succeeded} 成功 · ${overview.metrics.failed} 失败`} />
        <MetricCard label="待处理" value={String(overview.metrics.pendingConfirmation + overview.metrics.needsAttention)} detail={`${overview.metrics.pendingConfirmation} 待确认 · ${overview.metrics.needsAttention} 异常`} tone={overview.metrics.needsAttention ? "warning" : "normal"} />
        <MetricCard label="批量任务" value={String(overview.metrics.batchJobs)} detail={`${overview.metrics.batchPartialOrFailed} 个部分成功或需要处理`} tone={overview.metrics.batchPartialOrFailed ? "warning" : "normal"} />
      </section>
      <section className="admin-card"><h2>最近业务动作</h2><p className="muted">仅展示对象、动作、状态和错误摘要，不展示客户消息正文或字段值。</p>{overview.recentActions.length ? <div className="admin-table"><div className="admin-table__head"><span>时间与用户</span><span>业务动作</span><span>会话</span><span>状态</span></div>{overview.recentActions.map((action) => <div className="admin-table__row" key={action.id}><span>{formatTime(action.updatedAt)}<small>{action.salesforceUserId}</small></span><span><strong>{operationLabels[action.operation]} {action.objectApiName}</strong><small>{action.itemCount} 条 · 预览 v{action.version}</small></span><span>{action.conversationTitle}{action.error ? <small className="metadata-error">{action.error}</small> : null}</span><span><strong className={action.state === "succeeded" ? "connection-pill connection-pill--ok" : action.state === "failed" || action.state === "needs_attention" ? "connection-pill connection-pill--warn" : "connection-pill"}>{actionStateLabels[action.state]}</strong></span></div>)}</div> : <p className="empty-copy">当前 Org 尚无业务动作记录。</p>}</section>
      <section className="admin-card"><h2>批量任务</h2><p className="muted">展示批量执行结果；失败项可在原会话中修正后仅重试失败项。</p>{overview.batchJobs.length ? <div className="admin-table"><div className="admin-table__head"><span>更新时间</span><span>对象与操作</span><span>执行结果</span><span>状态</span></div>{overview.batchJobs.map((job) => <div className="admin-table__row" key={job.id}><span>{formatTime(job.updatedAt)}<small>尝试 {job.attemptCount} 次</small></span><span><strong>{operationLabels[job.operation]} {job.objectApiName}</strong><small>请求 {job.requested} 条</small></span><span>{job.succeeded} 成功 · {job.failed} 失败 · {job.excluded} 排除</span><span>{job.status}</span></div>)}</div> : <p className="empty-copy">当前 Org 尚无批量任务。</p>}</section>
    </> : <section className="admin-card"><p className="empty-copy">运行概览暂未加载，请刷新页面或检查审计权限。</p></section>}
    <section className="admin-card"><h2>管理审计事件</h2><p className="muted">记录连接、用户、模型、对象分析、业务场景、测试和发布操作；不记录密钥正文。</p>{events.length ? <div className="admin-table"><div className="admin-table__head"><span>时间</span><span>操作</span><span>目标</span><span>结果</span></div>{events.slice(0, 100).map((event) => <div className="admin-table__row" key={event.id}><span>{formatTime(event.createdAt)}</span><span>{event.action}</span><span>{event.targetType} · {event.targetId}</span><span>{event.outcome === "success" ? "成功" : "失败"}</span></div>)}</div> : <p className="empty-copy">暂无管理审计事件。</p>}</section>
  </div>;
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function bumpPatchVersion(version: string): string {
  const match = version.match(/^(\d+)\.(\d+)\.(\d+)/);
  return match ? `${match[1]}.${match[2]}.${Number(match[3]) + 1}` : "1.0.0";
}
